import Seo from "@/components/Seo";
import { useState, useEffect } from "react";
import { useNavigate, Routes, Route, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import { 
  Home, Users, CreditCard, BarChart3, LogOut, Menu, X, 
  Plus, TrendingUp, Ship, Waves, Fish, Wine, UserPlus, MapPin, Ticket, Trash2, Edit, Image, Power,
  Receipt, Wallet, ShieldCheck, Euro, TrendingDown, Calendar, Search, Download, Copy, CheckCircle2, KeyRound,
  ArrowUpRight, ArrowDownRight, Activity, PieChart as PieChartIcon, ExternalLink
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";

import { PassAlgarveLogo, PassAlgarveLogoCompact } from "@/components/PassAlgarveLogo";

const ATTRACTIONS = [
  { id: "ponta-piedade", name: "Ponta da Piedade", icon: Ship },
  { id: "benagil-cave", name: "Gruta de Benagil", icon: Waves },
  { id: "dolphin-watching", name: "Observacao dos Golfinhos", icon: Fish },
  { id: "bon-vivant", name: "Happy Hour Bon Vivant", icon: Wine }
];

// Dashboard Overview — Professional KPIs + Charts
const AdminOverview = () => {
  const { token, API } = useAuth();
  const [stats, setStats] = useState(null);
  const [dashData, setDashData] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [allAttractions, setAllAttractions] = useState([]);
  const [range, setRange] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const auth = { headers: { Authorization: `Bearer ${token}` } };
        const [s, d, ts, at] = await Promise.all([
          axios.get(`${API}/admin/stats`, auth),
          axios.get(`${API}/admin/stats/dashboard`, auth),
          axios.get(`${API}/admin/stats/timeseries?days=${range}`, auth),
          axios.get(`${API}/admin/attractions`, auth),
        ]);
        setStats(s.data);
        setDashData(d.data);
        setAllAttractions(at.data || []);
        setTimeseries((ts.data.series || []).map(p => ({
          ...p,
          dateLabel: new Date(p.date).toLocaleDateString("pt-PT", { day: "2-digit", month: "short" }),
        })));
      } catch (error) {
        console.error("Error fetching dashboard:", error);
        toast.error("Erro ao carregar dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, [API, token, range]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }

  const fmt = (v) => (Number(v || 0)).toFixed(2);
  const weekDelta = dashData?.week?.revenue_delta_pct;
  const salesDelta = dashData?.week?.sales_delta_pct;

  const kpis = [
    {
      label: "Receita Hoje",
      value: `€ ${fmt(stats?.revenue_today)}`,
      sub: `${stats?.sales_today || 0} vendas`,
      Icon: Euro, color: "emerald"
    },
    {
      label: "Receita Mês",
      value: `€ ${fmt(stats?.revenue_month)}`,
      sub: `${stats?.sales_month || 0} vendas`,
      Icon: Calendar, color: "blue"
    },
    {
      label: "Receita 7 dias",
      value: `€ ${fmt(dashData?.week?.revenue)}`,
      sub: `${dashData?.week?.sales || 0} vendas`,
      Icon: Activity, color: "indigo",
      delta: weekDelta,
    },
    {
      label: "Receita Total",
      value: `€ ${fmt(stats?.total_revenue)}`,
      sub: `${stats?.total_transactions || 0} transações`,
      Icon: TrendingUp, color: "gold"
    },
    {
      label: "Passaportes",
      value: stats?.total_passports || 0,
      sub: `${stats?.total_users || 0} utilizadores`,
      Icon: CreditCard, color: "purple"
    },
    {
      label: "Comissão Acumulada",
      value: `€ ${fmt(stats?.commission_total)}`,
      sub: "Após visitas validadas",
      Icon: Wallet, color: "gold"
    },
    {
      label: "A Pagar a Parceiros",
      value: `€ ${fmt(stats?.partner_payable_total)}`,
      sub: "Após visitas validadas",
      Icon: TrendingDown, color: "orange"
    },
    {
      label: "Vendas (7d) vs ant.",
      value: salesDelta == null ? "—" : `${salesDelta > 0 ? "+" : ""}${salesDelta}%`,
      sub: `${dashData?.week?.sales || 0} vs semana ant.`,
      Icon: salesDelta != null && salesDelta < 0 ? ArrowDownRight : ArrowUpRight,
      color: salesDelta != null && salesDelta < 0 ? "red" : "emerald",
    },
  ];

  const colorMap = {
    emerald: "bg-emerald-100 text-emerald-600",
    blue: "bg-blue-100 text-blue-600",
    indigo: "bg-indigo-100 text-indigo-600",
    gold: "bg-gold/15 text-gold",
    purple: "bg-purple-100 text-purple-600",
    orange: "bg-orange-100 text-orange-600",
    red: "bg-red-100 text-red-600",
  };

  const PIE_COLORS = ["#D4AF37", "#1e3a8a", "#0d9488", "#a855f7", "#f97316", "#ef4444", "#06b6d4"];
  const statusData = Object.entries(dashData?.sales_by_status || {}).map(([k, v]) => ({ name: k, value: v }));
  const STATUS_COLORS = { paid: "#10b981", initiated: "#f59e0b", expired: "#9ca3af", failed: "#ef4444" };

  return (
    <div className="space-y-8">
      <Seo noindex title="Dashboard Administrativo" />
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl text-navy mb-2">Dashboard Administrativo</h1>
          <p className="text-navy/60 font-body">Visão geral em tempo real do PASS ALGARVE</p>
        </div>
        <div className="flex items-center gap-2 bg-white rounded-full p-1 border border-gold/20 shadow-sm" data-testid="range-toggle">
          {[7, 30, 90].map(d => (
            <button
              key={d}
              onClick={() => setRange(d)}
              className={`text-xs font-body px-3 py-1.5 rounded-full transition ${
                range === d ? "bg-gold text-white" : "text-navy/60 hover:text-navy"
              }`}
              data-testid={`range-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* KPI Grid (8 cards in 4 cols) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map(({ label, value, sub, Icon, color, delta }) => (
          <Card key={label} className="bg-white border-gold/20 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="pt-5">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-navy/60 text-xs font-body uppercase tracking-wide">{label}</p>
                  <p className="font-heading text-2xl text-navy mt-1 truncate">{value}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {delta != null && (
                      <span
                        className={`inline-flex items-center text-[11px] font-semibold px-1.5 py-0.5 rounded ${
                          delta >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
                        }`}
                      >
                        {delta >= 0 ? "↑" : "↓"} {Math.abs(delta)}%
                      </span>
                    )}
                    {sub && <p className="text-navy/40 text-xs font-body truncate">{sub}</p>}
                  </div>
                </div>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${colorMap[color]}`}>
                  <Icon className="w-5 h-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Revenue area chart */}
      <Card className="bg-white border-gold/20 shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-xl text-navy">Receita ({range} dias)</CardTitle>
            <div className="text-xs text-navy/50 font-body">
              Total: € {fmt(timeseries.reduce((s, x) => s + (x.revenue || 0), 0))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-72" data-testid="chart-revenue">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timeseries} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="grad-rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="dateLabel" tick={{ fontSize: 11, fill: "#64748b" }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `€${v}`} />
                <Tooltip
                  contentStyle={{ background: "#0f172a", border: "none", borderRadius: 8, color: "#fff", fontSize: 12 }}
                  formatter={(value, name) => name === "revenue" ? [`€${Number(value).toFixed(2)}`, "Receita"] : [value, "Vendas"]}
                  labelFormatter={(l) => `Data: ${l}`}
                />
                <Area type="monotone" dataKey="revenue" stroke="#D4AF37" strokeWidth={2} fill="url(#grad-rev)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* 2-col: sales by passport type + status pie */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-white border-gold/20 shadow-sm lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-xl text-navy">Vendas por Tipo de Passaporte</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64" data-testid="chart-by-type">
              {(dashData?.sales_by_passport_type || []).length === 0 ? (
                <div className="h-full flex items-center justify-center text-navy/40 text-sm">
                  Sem vendas ainda
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dashData.sales_by_passport_type} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                    <Tooltip
                      contentStyle={{ background: "#0f172a", border: "none", borderRadius: 8, color: "#fff", fontSize: 12 }}
                      formatter={(v, n) => n === "revenue" ? [`€${Number(v).toFixed(2)}`, "Receita"] : [v, "Vendas"]}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="count" name="Vendas" fill="#1e3a8a" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="revenue" name="Receita (€)" fill="#D4AF37" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-gold/20 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-xl text-navy">Status Transações</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64" data-testid="chart-status">
              {statusData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-navy/40 text-sm">
                  Sem transações
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%" cy="50%"
                      innerRadius={45} outerRadius={80}
                      paddingAngle={3}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {statusData.map((entry, i) => (
                        <Cell key={i} fill={STATUS_COLORS[entry.name] || PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#0f172a", border: "none", borderRadius: 8, color: "#fff", fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 2-col: top attractions + recent transactions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-white border-gold/20 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-xl text-navy">Top Atrações (Visitas)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72" data-testid="chart-top-attractions">
              {(dashData?.top_attractions || []).length === 0 ? (
                <div className="h-full flex items-center justify-center text-navy/40 text-sm">
                  Sem visitas validadas ainda
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={dashData.top_attractions.slice(0, 8)}
                    layout="vertical"
                    margin={{ top: 5, right: 20, left: 80, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} width={120} />
                    <Tooltip
                      contentStyle={{ background: "#0f172a", border: "none", borderRadius: 8, color: "#fff", fontSize: 12 }}
                      formatter={(v) => [v, "Visitas"]}
                    />
                    <Bar dataKey="visits" name="Visitas" fill="#0d9488" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-gold/20 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-xl text-navy">Últimas Transações</CardTitle>
              <a href="/admin/transactions" className="text-xs text-gold hover:underline font-body" data-testid="link-all-transactions">Ver todas →</a>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3" data-testid="recent-transactions">
              {(dashData?.recent_transactions || []).length === 0 ? (
                <div className="h-24 flex items-center justify-center text-navy/40 text-sm">
                  Nenhuma transação registada
                </div>
              ) : (
                dashData.recent_transactions.map((t) => {
                  const statusColor = {
                    paid: "bg-emerald-100 text-emerald-700",
                    initiated: "bg-amber-100 text-amber-700",
                    expired: "bg-gray-100 text-gray-600",
                    failed: "bg-red-100 text-red-700",
                  }[t.payment_status] || "bg-gray-100 text-gray-600";
                  return (
                    <div key={t.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3" data-testid={`recent-tx-${t.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-navy truncate">{t.passport_type_name || "—"}</p>
                        <p className="text-xs text-navy/50 truncate">{t.user_email}</p>
                        <p className="text-[10px] text-navy/40 font-mono">{(t.created_at || "").substring(0, 16).replace("T", " ")}</p>
                      </div>
                      <div className="text-right ml-3">
                        <p className="text-sm font-heading text-navy">€ {fmt(t.amount)}</p>
                        <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full ${statusColor}`}>{t.payment_status}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Attraction Visits compact summary */}
      <Card className="bg-white border-gold/20 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-xl text-navy">Visitas por Atração (totais)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {(allAttractions.length ? allAttractions : ATTRACTIONS).map((attraction) => {
              const visits = stats?.attraction_visits?.[attraction.id] || 0;
              const legacy = ATTRACTIONS.find(l => l.id === attraction.id);
              const Icon = attraction.icon || legacy?.icon || MapPin;
              return (
                <div key={attraction.id} className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-gold" />
                    </div>
                    <span className="font-heading text-2xl text-navy">{visits}</span>
                  </div>
                  <p className="text-navy/60 text-sm font-body">{attraction.name}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Users Management
const UsersManagement = () => {
  const { token, API } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Erro ao carregar usuarios");
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      await axios.put(
        `${API}/admin/users/${userId}/role?role=${newRole}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Role atualizado com sucesso");
      fetchUsers();
    } catch (error) {
      console.error("Error updating role:", error);
      toast.error("Erro ao atualizar role");
    }
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setEditForm({ name: user.name || "", email: user.email || "", password: "" });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { name: editForm.name, email: editForm.email };
      if (editForm.password.trim()) payload.password = editForm.password.trim();
      await axios.put(`${API}/admin/users/${editingUser.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Dados do utilizador atualizados");
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error(error.response?.data?.detail || "Erro ao atualizar dados");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl text-navy mb-2">Gestao de Usuarios</h1>
        <p className="text-navy/60 font-body">Gerencie todos os usuarios da plataforma</p>
      </div>

      {editingUser && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditingUser(null)} data-testid="edit-user-modal">
          <Card className="bg-white border-gold/20 shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-xl text-navy flex items-center gap-2">
                <Edit className="w-4 h-4 text-gold" /> Alterar dados do utilizador
              </CardTitle>
              <p className="text-navy/50 text-xs">{editingUser.email} · {editingUser.role}</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveEdit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="edit-user-name">Nome</Label>
                  <Input
                    id="edit-user-name"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    required
                    className="border-gold/30 focus:border-gold"
                    data-testid="edit-user-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-user-email">Email</Label>
                  <Input
                    id="edit-user-email"
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    required
                    className="border-gold/30 focus:border-gold"
                    data-testid="edit-user-email"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-user-password">Nova senha (opcional)</Label>
                  <Input
                    id="edit-user-password"
                    type="text"
                    value={editForm.password}
                    onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                    placeholder="Deixe vazio para manter a atual"
                    className="border-gold/30 focus:border-gold"
                    data-testid="edit-user-password"
                  />
                  <p className="text-[11px] text-navy/50">Mínimo 6 caracteres. Só será alterada se preencher este campo.</p>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="submit" disabled={saving} className="bg-gold hover:bg-gold-dark text-white" data-testid="edit-user-save-btn">
                    {saving ? "A guardar..." : "Guardar alterações"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setEditingUser(null)} data-testid="edit-user-cancel-btn">
                    Cancelar
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      <Card className="bg-white border-gold/20 shadow-lg">
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-body">Nome</TableHead>
                <TableHead className="font-body">Email</TableHead>
                <TableHead className="font-body">Role</TableHead>
                <TableHead className="font-body">Data Cadastro</TableHead>
                <TableHead className="font-body">Acoes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Badge className={
                      user.role === "admin" ? "bg-purple-100 text-purple-700" :
                      user.role === "partner" ? "bg-blue-100 text-blue-700" :
                      "bg-gray-100 text-gray-700"
                    }>
                      {user.role}
                    </Badge>
                  </TableCell>
                  <TableCell>{new Date(user.created_at).toLocaleDateString('pt-PT')}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Select
                        defaultValue={user.role}
                        onValueChange={(value) => updateUserRole(user.id, value)}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="customer">Customer</SelectItem>
                          <SelectItem value="partner">Partner</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEdit(user)}
                        className="border-gold/40 text-navy hover:bg-gold/10 whitespace-nowrap"
                        data-testid={`edit-user-btn-${user.id}`}
                      >
                        <Edit className="w-3.5 h-3.5 mr-1.5" /> Alterar dados
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// =====================================================
// PartnersManagement — Professional
// =====================================================
const PartnersManagement = () => {
  const { token, API } = useAuth();
  const [partners, setPartners] = useState([]);
  const [attractions, setAttractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [resetPwdFor, setResetPwdFor] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({ name: "", email: "", password: "" });
  const [editForm, setEditForm] = useState({ name: "", email: "" });
  const [newPwd, setNewPwd] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [pRes, aRes] = await Promise.all([
        axios.get(`${API}/admin/partners`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/attractions`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setPartners(pRes.data || []);
      setAttractions(aRes.data || []);
    } catch (e) {
      toast.error("Erro ao carregar parceiros");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const filtered = partners.filter(p => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (p.name || "").toLowerCase().includes(s) || (p.email || "").toLowerCase().includes(s);
  });

  const totalNetDue = partners.reduce((sum, p) => sum + (p.stats?.net_due || 0), 0);
  const partnersConnected = partners.filter(p => p.stripe_payouts_enabled).length;

  const handleCreate = async (e) => {
    e.preventDefault();
    if (formData.password.length < 8) {
      toast.error("Senha deve ter pelo menos 8 caracteres");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/partners`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Parceiro criado");
      setShowCreateForm(false);
      setFormData({ name: "", email: "", password: "" });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar parceiro");
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditForm({ name: p.name, email: p.email });
  };

  const handleEdit = async (e, id) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.put(`${API}/admin/partners/${id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Parceiro atualizado");
      setEditingId(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao atualizar");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async (e, id) => {
    e.preventDefault();
    if (newPwd.length < 8) {
      toast.error("Senha deve ter pelo menos 8 caracteres");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/partners/${id}/reset-password`,
        { password: newPwd },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Senha redefinida e enviada ao parceiro");
      setResetPwdFor(null);
      setNewPwd("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao redefinir senha");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    setSubmitting(true);
    try {
      await axios.delete(`${API}/admin/partners/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Parceiro removido");
      setDeleteConfirm(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao remover parceiro");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetStripe = async (id, email) => {
    if (!window.confirm(`Resetar a conta Stripe Connect de ${email}?\n\nIsto vai apagar o stripe_account_id atual. O parceiro terá de iniciar um NOVO onboarding Stripe.\n\nUsa esta opção quando:\n• A conta foi criada em modo TEST e queres mudar para LIVE\n• O parceiro está preso num estado inválido de onboarding\n\nNão afeta visitas/transfers já registadas.`)) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/partners/${id}/reset-stripe`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data?.message || "Conta Stripe resetada");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao resetar Stripe");
    } finally {
      setSubmitting(false);
    }
  };

  const unassignedAttractions = attractions.filter(a => !a.partner_id);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl text-navy mb-2">Parceiros</h1>
          <p className="text-navy/60 font-body">Gerencie contas de parceiros e o estado da integração Stripe</p>
        </div>
        <Button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="bg-gold hover:bg-gold-dark text-white rounded-full"
          data-testid="add-partner-btn"
        >
          <UserPlus className="mr-2 h-4 w-4" />
          Novo Parceiro
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border-gold/20 shadow-sm">
          <CardContent className="pt-5 pb-5">
            <p className="text-navy/60 text-sm font-body">Total Parceiros</p>
            <p className="font-heading text-2xl text-navy">{partners.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-gold/20 shadow-sm">
          <CardContent className="pt-5 pb-5">
            <p className="text-navy/60 text-sm font-body">Conectados ao Stripe</p>
            <p className="font-heading text-2xl text-emerald-700">{partnersConnected} / {partners.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-gold/20 shadow-sm">
          <CardContent className="pt-5 pb-5">
            <p className="text-navy/60 text-sm font-body">Valor Net Acumulado</p>
            <p className="font-heading text-2xl text-emerald-700">€ {totalNetDue.toFixed(2)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Unassigned attractions warning */}
      {unassignedAttractions.length > 0 && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-amber-800 font-body">
              <strong>⚠ {unassignedAttractions.length} atraç{unassignedAttractions.length === 1 ? "ão" : "ões"} sem parceiro:</strong>{" "}
              {unassignedAttractions.map(a => a.name).join(", ")}.
              {" "}Atribua um parceiro em <span className="font-semibold">Atrações</span> para que o split de pagamento funcione.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Create form */}
      {showCreateForm && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="bg-white border-gold/20 shadow-lg">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-navy">Criar Novo Parceiro</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="p-name">Nome do parceiro / empresa</Label>
                    <Input id="p-name" required value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="border-gold/30" data-testid="partner-name-input"
                      placeholder="Ex: Bon Vivant Lagos" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="p-email">Email de acesso</Label>
                    <Input id="p-email" type="email" required value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="border-gold/30" data-testid="partner-email-input"
                      placeholder="parceiro@empresa.pt" />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="p-pwd">Senha inicial (mínimo 8 caracteres)</Label>
                    <Input id="p-pwd" type="text" required value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="border-gold/30 font-mono" data-testid="partner-password-input"
                      placeholder="O parceiro pode alterar depois" />
                    <p className="text-xs text-navy/50">O parceiro usará estas credenciais para entrar e completar o onboarding Stripe.</p>
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="submit" disabled={submitting}
                    className="bg-gold hover:bg-gold-dark text-white rounded-full" data-testid="create-partner-btn">
                    {submitting ? "Criando..." : "Criar Parceiro"}
                  </Button>
                  <Button type="button" variant="outline"
                    onClick={() => { setShowCreateForm(false); setFormData({ name: "", email: "", password: "" }); }}
                    className="rounded-full">
                    Cancelar
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/40" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Pesquisar por nome ou email..." className="pl-10 border-gold/30" data-testid="partners-search" />
      </div>

      {/* Partners list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-gold"></div>
        </div>
      ) : filtered.length === 0 ? (
        <Card className="bg-white border-gold/20">
          <CardContent className="pt-12 pb-12 text-center text-navy/50">
            {partners.length === 0 ? "Nenhum parceiro criado ainda." : "Nenhum parceiro encontrado."}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filtered.map((p) => {
            const stripeConnected = p.stripe_payouts_enabled;
            const stripePending = p.stripe_account_id && !p.stripe_payouts_enabled;
            return (
              <Card key={p.id} className="bg-white border-gold/20 shadow-md hover:shadow-lg transition-shadow" data-testid={`partner-card-${p.id}`}>
                <CardContent className="pt-5 pb-5">
                  {/* Edit mode */}
                  {editingId === p.id ? (
                    <form onSubmit={(e) => handleEdit(e, p.id)} className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} placeholder="Nome" required className="border-gold/30" />
                        <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} placeholder="Email" required className="border-gold/30" />
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" disabled={submitting} size="sm" className="bg-gold hover:bg-gold-dark text-white rounded-full">Guardar</Button>
                        <Button type="button" size="sm" variant="outline" onClick={() => setEditingId(null)} className="rounded-full">Cancelar</Button>
                      </div>
                    </form>
                  ) : resetPwdFor === p.id ? (
                    <form onSubmit={(e) => handleResetPassword(e, p.id)} className="space-y-3">
                      <p className="text-sm text-navy/70">Redefinir senha para <strong>{p.email}</strong>:</p>
                      <Input type="text" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} placeholder="Nova senha (mínimo 8 chars)" required className="border-gold/30 font-mono" data-testid={`partner-newpwd-${p.id}`} />
                      <div className="flex gap-2">
                        <Button type="submit" disabled={submitting} size="sm" className="bg-gold hover:bg-gold-dark text-white rounded-full">Redefinir</Button>
                        <Button type="button" size="sm" variant="outline" onClick={() => { setResetPwdFor(null); setNewPwd(""); }} className="rounded-full">Cancelar</Button>
                      </div>
                    </form>
                  ) : deleteConfirm === p.id ? (
                    <div className="space-y-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-800">Tem certeza? <strong>{p.name}</strong> será removido permanentemente. As atrações associadas continuarão (sem parceiro).</p>
                      <div className="flex gap-2">
                        <Button onClick={() => handleDelete(p.id)} disabled={submitting} size="sm" className="bg-red-600 hover:bg-red-700 text-white rounded-full" data-testid={`partner-delete-confirm-${p.id}`}>Sim, remover</Button>
                        <Button size="sm" variant="outline" onClick={() => setDeleteConfirm(null)} className="rounded-full">Cancelar</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        <div className="w-12 h-12 rounded-full bg-gold/15 flex items-center justify-center flex-shrink-0">
                          <UserPlus className="w-6 h-6 text-gold" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-heading text-lg text-navy font-semibold">{p.name}</p>
                            {stripeConnected ? (
                              <Badge className="bg-emerald-100 text-emerald-700">Stripe ativo</Badge>
                            ) : stripePending ? (
                              <Badge className="bg-orange-100 text-orange-700">Stripe pendente</Badge>
                            ) : (
                              <Badge className="bg-gray-100 text-gray-600">Sem Stripe</Badge>
                            )}
                          </div>
                          <p className="text-sm text-navy/60">{p.email}</p>
                          {p.stripe_account_id && (
                            <p className="text-xs font-mono text-navy/40 mt-0.5">{p.stripe_account_id}</p>
                          )}
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {(p.attractions || []).length === 0 ? (
                              <span className="text-xs text-amber-700 italic">Sem atrações atribuídas</span>
                            ) : (
                              p.attractions.map(a => (
                                <Link
                                  key={a.id}
                                  to={`/atracao/${a.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Abrir página pública da atração"
                                  data-testid={`partner-attraction-link-${a.id}`}
                                  className="group"
                                >
                                  <Badge
                                    variant="outline"
                                    className="bg-gold/10 border-gold/30 text-navy text-xs hover:bg-gold/20 hover:border-gold transition-colors cursor-pointer inline-flex items-center gap-1"
                                  >
                                    {a.name} · €{(a.price_net || 0).toFixed(0)}
                                    <ExternalLink className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity" />
                                  </Badge>
                                </Link>
                              ))
                            )}
                          </div>
                          <div className="mt-2 grid grid-cols-3 gap-3 text-xs">
                            <div>
                              <span className="text-navy/50">Visitas: </span>
                              <span className="font-semibold text-navy">{p.stats?.visits || 0}</span>
                            </div>
                            <div>
                              <span className="text-navy/50">Net acumulado: </span>
                              <span className="font-semibold text-emerald-700">€{(p.stats?.net_due || 0).toFixed(2)}</span>
                            </div>
                            <div>
                              <span className="text-navy/50">Transfers: </span>
                              <span className="font-semibold text-navy">{p.stats?.transfers_count || 0}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-1.5 lg:flex-col lg:items-end flex-wrap">
                        <Button size="sm" variant="outline" onClick={() => startEdit(p)}
                          className="text-blue-600 border-blue-300 hover:bg-blue-50 rounded-full"
                          data-testid={`partner-edit-${p.id}`}>
                          <Edit className="w-3.5 h-3.5 mr-1" /> Editar
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setResetPwdFor(p.id); setNewPwd(""); }}
                          className="text-navy border-gold/30 hover:bg-gold/10 rounded-full"
                          data-testid={`partner-resetpwd-${p.id}`}>
                          <KeyRound className="w-3.5 h-3.5 mr-1" /> Senha
                        </Button>
                        {p.stripe_account_id && (
                          <Button size="sm" variant="outline" onClick={() => handleResetStripe(p.id, p.email)}
                            className="text-orange-600 border-orange-300 hover:bg-orange-50 rounded-full"
                            data-testid={`partner-resetstripe-${p.id}`}
                            title="Apagar conta Stripe Connect para o parceiro iniciar um novo onboarding (útil para mudar de TEST para LIVE)">
                            <CreditCard className="w-3.5 h-3.5 mr-1" /> Reset Stripe
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => setDeleteConfirm(p.id)}
                          className="text-red-600 border-red-300 hover:bg-red-50 rounded-full"
                          data-testid={`partner-delete-${p.id}`}>
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Remover
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Passports Management
const PassportsManagement = () => {
  const { token, API } = useAuth();
  const [passports, setPassports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPassports();
  }, []);

  const fetchPassports = async () => {
    try {
      const response = await axios.get(`${API}/admin/passports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPassports(response.data);
    } catch (error) {
      console.error("Error fetching passports:", error);
      toast.error("Erro ao carregar passaportes");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl text-navy mb-2">Passaportes</h1>
        <p className="text-navy/60 font-body">Todos os passaportes emitidos</p>
      </div>

      <Card className="bg-white border-gold/20 shadow-lg overflow-hidden">
        <CardContent className="pt-6 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-body">QR Code</TableHead>
                <TableHead className="font-body">Titular</TableHead>
                <TableHead className="font-body">Email</TableHead>
                <TableHead className="font-body">Compra</TableHead>
                <TableHead className="font-body">Validade</TableHead>
                <TableHead className="font-body">Visitas</TableHead>
                <TableHead className="font-body">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {passports.map((passport) => (
                <TableRow key={passport.id}>
                  <TableCell className="font-mono text-sm">{passport.qr_code}</TableCell>
                  <TableCell className="font-medium">{passport.user_name}</TableCell>
                  <TableCell>{passport.user_email}</TableCell>
                  <TableCell>{new Date(passport.purchase_date).toLocaleDateString('pt-PT')}</TableCell>
                  <TableCell>{new Date(passport.expiry_date).toLocaleDateString('pt-PT')}</TableCell>
                  <TableCell>{passport.visits?.length || 0}/4</TableCell>
                  <TableCell>
                    <Badge className={
                      passport.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
                    }>
                      {passport.status === "active" ? "Ativo" : "Inativo"}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// Attractions Management
const EMPTY_BOOKING_FIELDS = {
  requires_booking: false,
  booking_methods: [],
  booking_agency_name: "",
  booking_address: "",
  booking_maps_url: "",
  booking_phone: "",
  booking_whatsapp: "",
  booking_email: "",
  booking_external_url: "",
  booking_instructions: ""
};

const BOOKING_METHOD_OPTIONS = [
  { value: "presencial", label: "Presencial na agência" },
  { value: "phone", label: "Telefone" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "email", label: "E-mail" },
  { value: "external", label: "Site externo" },
];

const EMPTY_ATTRACTION_FORM = {
  name: "",
  description: "",
  images: [],
  full_description: "",
  duration: "1h",
  highlights: "",
  price_total: "",
  price_net: "",
  price_commission: "",
  partner_id: "",
  name_en: "",
  description_en: "",
  full_description_en: "",
  highlights_en: "",
  seo_title: "",
  seo_description: "",
  slug: "",
  seo_keyword: "",
  social_image: "",
  seo_title_en: "",
  seo_description_en: "",
  slug_en: "",
  seo_keyword_en: "",
  social_image_en: "",
  indexable: true,
  ...EMPTY_BOOKING_FIELDS
};

const AttractionsManagement = () => {
  const { token, API } = useAuth();
  const [attractions, setAttractions] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [seoTab, setSeoTab] = useState("pt");
  const [editingAttraction, setEditingAttraction] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [formData, setFormData] = useState({ ...EMPTY_ATTRACTION_FORM });

  const handleTranslateAll = async () => {
    setTranslating(true);
    try {
      const res = await axios.post(`${API}/admin/translate-content`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = res.data;
      toast.success(`Tradução concluída: ${d.attractions_translated} atrações e ${d.types_translated} passaportes traduzidos${d.errors ? ` (${d.errors} erros)` : ""}`);
      fetchAttractions();
    } catch (error) {
      console.error("Error translating:", error);
      toast.error(error.response?.data?.detail || "Erro ao traduzir conteúdo");
    } finally {
      setTranslating(false);
    }
  };

  useEffect(() => {
    fetchAttractions();
    fetchPartners();
  }, []);

  const fetchAttractions = async () => {
    try {
      const response = await axios.get(`${API}/admin/attractions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttractions(response.data);
    } catch (error) {
      console.error("Error fetching attractions:", error);
      toast.error("Erro ao carregar atrações");
    } finally {
      setLoading(false);
    }
  };

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API}/admin/partners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartners(response.data);
    } catch (error) {
      console.error("Error fetching partners:", error);
    }
  };

  // Auto-calc helpers: when total + commission change, net = total - commission
  const handlePriceTotalChange = (v) => {
    const total = parseFloat(v) || 0;
    const commission = parseFloat(formData.price_commission) || 0;
    const net = Math.max(0, +(total - commission).toFixed(2));
    setFormData({ ...formData, price_total: v, price_net: net ? String(net) : "" });
  };

  const handleCommissionChange = (v) => {
    const total = parseFloat(formData.price_total) || 0;
    const commission = parseFloat(v) || 0;
    const net = Math.max(0, +(total - commission).toFixed(2));
    setFormData({ ...formData, price_commission: v, price_net: net ? String(net) : "" });
  };

  const handleNetChange = (v) => {
    const total = parseFloat(formData.price_total) || 0;
    const net = parseFloat(v) || 0;
    const commission = Math.max(0, +(total - net).toFixed(2));
    setFormData({ ...formData, price_net: v, price_commission: commission ? String(commission) : "" });
  };

  const applyCommissionPercent = (percent) => {
    const total = parseFloat(formData.price_total) || 0;
    if (!total) {
      toast.error("Preencha primeiro o Valor Total");
      return;
    }
    const commission = +(total * (percent / 100)).toFixed(2);
    const net = +(total - commission).toFixed(2);
    setFormData({ ...formData, price_commission: String(commission), price_net: String(net) });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    if (formData.images.length + files.length > 20) {
      toast.error("Maximo de 20 imagens por atracao");
      return;
    }

    setUploading(true);
    
    for (const file of files) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append("file", file);
        
        const response = await axios.post(`${API}/admin/upload-image`, formDataUpload, {
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        });
        
        if (response.data.success) {
          setFormData(prev => ({
            ...prev,
            images: [...prev.images, response.data.url]
          }));
        }
      } catch (error) {
        console.error("Upload error:", error);
        toast.error(`Erro ao enviar ${file.name}`);
      }
    }
    
    setUploading(false);
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const [imageUrlInput, setImageUrlInput] = useState("");
  const addImageUrl = () => {
    const url = (imageUrlInput || "").trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) {
      toast.error("URL deve começar com http:// ou https://");
      return;
    }
    if (formData.images.length >= 20) {
      toast.error("Máximo 20 imagens");
      return;
    }
    setFormData(prev => ({ ...prev, images: [...prev.images, url] }));
    setImageUrlInput("");
    toast.success("URL adicionada");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.images.length === 0) {
      toast.error("Adicione pelo menos uma imagem");
      return;
    }
    
    // Validate split numerically client-side too
    const total = parseFloat(formData.price_total) || 0;
    const net = parseFloat(formData.price_net) || 0;
    const commission = parseFloat(formData.price_commission) || 0;
    if (total > 0 && Math.abs(total - (net + commission)) > 0.01) {
      toast.error(`Split inválido: Total (€${total.toFixed(2)}) ≠ Net (€${net.toFixed(2)}) + Comissão (€${commission.toFixed(2)})`);
      return;
    }
    
    const payload = {
      name: formData.name,
      description: formData.description,
      images: formData.images,
      image_url: formData.images[0] || "",
      full_description: formData.full_description,
      duration: formData.duration,
      highlights: formData.highlights.split(",").map(h => h.trim()).filter(h => h),
      name_en: formData.name_en,
      description_en: formData.description_en,
      full_description_en: formData.full_description_en,
      highlights_en: formData.highlights_en.split(",").map(h => h.trim()).filter(h => h),
      seo_title: formData.seo_title,
      seo_description: formData.seo_description,
      slug: formData.slug.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, ""),
      seo_keyword: formData.seo_keyword,
      social_image: formData.social_image,
      seo_title_en: formData.seo_title_en,
      seo_description_en: formData.seo_description_en,
      slug_en: formData.slug_en.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, ""),
      seo_keyword_en: formData.seo_keyword_en,
      social_image_en: formData.social_image_en,
      indexable: formData.indexable,
      price_total: total,
      price_net: net,
      price_commission: commission,
      partner_id: formData.partner_id || null,
      requires_booking: !!formData.requires_booking,
      booking: formData.requires_booking ? {
        methods: formData.booking_methods,
        agency_name: formData.booking_agency_name,
        address: formData.booking_address,
        maps_url: formData.booking_maps_url,
        phone: formData.booking_phone,
        whatsapp: formData.booking_whatsapp,
        email: formData.booking_email,
        external_url: formData.booking_external_url,
        instructions: formData.booking_instructions
      } : undefined
    };

    try {
      if (editingAttraction) {
        await axios.put(`${API}/admin/attractions/${editingAttraction.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Atração atualizada com sucesso");
      } else {
        await axios.post(`${API}/admin/attractions`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Atração criada com sucesso");
      }
      
      setShowForm(false);
      setEditingAttraction(null);
      setFormData({ ...EMPTY_ATTRACTION_FORM });
      fetchAttractions();
    } catch (error) {
      console.error("Error saving attraction:", error);
      toast.error(error.response?.data?.detail || "Erro ao salvar atração");
    }
  };

  const handleEdit = (e, attraction) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingAttraction(attraction);
    setFormData({
      name: attraction.name,
      description: attraction.description,
      images: attraction.images || (attraction.image_url ? [attraction.image_url] : []),
      full_description: attraction.full_description || "",
      duration: attraction.duration || "1h",
      highlights: (attraction.highlights || []).join(", "),
      name_en: attraction.name_en || "",
      description_en: attraction.description_en || "",
      full_description_en: attraction.full_description_en || "",
      highlights_en: (attraction.highlights_en || []).join(", "),
      seo_title: attraction.seo_title || "",
      seo_description: attraction.seo_description || "",
      slug: attraction.slug || "",
      seo_keyword: attraction.seo_keyword || "",
      social_image: attraction.social_image || "",
      seo_title_en: attraction.seo_title_en || "",
      seo_description_en: attraction.seo_description_en || "",
      slug_en: attraction.slug_en || "",
      seo_keyword_en: attraction.seo_keyword_en || "",
      social_image_en: attraction.social_image_en || "",
      indexable: attraction.indexable !== false,
      price_total: attraction.price_total != null ? String(attraction.price_total) : "",
      price_net: attraction.price_net != null ? String(attraction.price_net) : "",
      price_commission: attraction.price_commission != null ? String(attraction.price_commission) : "",
      partner_id: attraction.partner_id || "",
      requires_booking: !!attraction.requires_booking,
      booking_methods: attraction.booking?.methods || [],
      booking_agency_name: attraction.booking?.agency_name || "",
      booking_address: attraction.booking?.address || "",
      booking_maps_url: attraction.booking?.maps_url || "",
      booking_phone: attraction.booking?.phone || "",
      booking_whatsapp: attraction.booking?.whatsapp || "",
      booking_email: attraction.booking?.email || "",
      booking_external_url: attraction.booking?.external_url || "",
      booking_instructions: attraction.booking?.instructions || ""
    });
    setShowForm(true);
  };

  const handleDelete = async (e, attractionId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Tem certeza que deseja remover esta atracao?")) return;
    
    try {
      await axios.delete(`${API}/admin/attractions/${attractionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Atracao removida com sucesso");
      fetchAttractions();
    } catch (error) {
      console.error("Error deleting attraction:", error);
      toast.error("Erro ao remover atracao");
    }
  };

  const handleToggleActive = async (e, attraction) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await axios.put(`${API}/admin/attractions/${attraction.id}`, {
        ...attraction,
        is_active: !attraction.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(attraction.is_active ? "Atracao desativada" : "Atracao ativada");
      fetchAttractions();
    } catch (error) {
      console.error("Error toggling attraction status:", error);
      toast.error("Erro ao alterar status");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl text-navy mb-2">Gestao de Atracoes</h1>
          <p className="text-navy/60 font-body">Cadastre e gerencie as atracoes do passaporte</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleTranslateAll}
            disabled={translating}
            variant="outline"
            className="border-gold/50 text-navy hover:bg-gold/10 rounded-full"
            data-testid="translate-content-btn"
          >
            {translating ? "A traduzir..." : "🌐 Traduzir p/ Inglês (IA)"}
          </Button>
          <Button 
            onClick={() => {
              setEditingAttraction(null);
              setFormData({ ...EMPTY_ATTRACTION_FORM });
              setShowForm(!showForm);
            }}
            className="bg-gold hover:bg-gold-dark text-white rounded-full"
            data-testid="add-attraction-btn"
          >
            <Plus className="mr-2 h-4 w-4" />
            Nova Atração
          </Button>
        </div>
      </div>

      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="bg-white border-gold/20 shadow-lg">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-navy">
                {editingAttraction ? "Editar Atracao" : "Nova Atracao"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Image Upload Section */}
                <div className="space-y-3">
                  <Label>Fotos da Atracao (max. 5)</Label>
                  <div className="flex flex-wrap gap-3">
                    {formData.images.map((img, index) => (
                      <div key={index} className="relative w-24 h-24 rounded-lg overflow-hidden border-2 border-gold/30">
                        <img 
                          src={img.startsWith('/api') ? `${API.replace('/api', '')}${img}` : img} 
                          alt={`Foto ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        {index === 0 && (
                          <span className="absolute bottom-0 left-0 right-0 bg-gold text-white text-xs text-center py-0.5">
                            Principal
                          </span>
                        )}
                      </div>
                    ))}
                    {formData.images.length < 20 && (
                      <label className="w-24 h-24 rounded-lg border-2 border-dashed border-gold/30 flex flex-col items-center justify-center cursor-pointer hover:border-gold hover:bg-gold/5 transition-all">
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageUpload}
                          className="hidden"
                          disabled={uploading}
                        />
                        {uploading ? (
                          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-gold"></div>
                        ) : (
                          <>
                            <Image className="w-6 h-6 text-gold/50 mb-1" />
                            <span className="text-xs text-gold/50">Adicionar</span>
                          </>
                        )}
                      </label>
                    )}
                  </div>
                  <p className="text-xs text-navy/50">A primeira imagem sera a principal. Arraste para reordenar.</p>

                  {/* External URL input — survives container restarts */}
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <Label className="text-xs text-amber-900 font-semibold flex items-center gap-1">
                      Adicionar via URL externa (recomendado)
                    </Label>
                    <p className="text-[11px] text-amber-800/80 mb-2">
                      Imagens enviadas por upload podem desaparecer após redeploys. Use URLs do Unsplash, Imgur ou Google Drive (público) para garantir persistência.
                    </p>
                    <div className="flex gap-2">
                      <Input
                        type="url"
                        placeholder="https://images.unsplash.com/photo-..."
                        value={imageUrlInput}
                        onChange={(e) => setImageUrlInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addImageUrl(); } }}
                        className="border-amber-300 bg-white text-sm"
                        data-testid="image-url-input"
                      />
                      <Button
                        type="button"
                        onClick={addImageUrl}
                        disabled={formData.images.length >= 20}
                        className="bg-amber-600 hover:bg-amber-700 text-white text-sm whitespace-nowrap"
                        data-testid="add-image-url-btn"
                      >
                        Adicionar URL
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nome da Atracao</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="border-gold/30 focus:border-gold"
                      placeholder="Ex: Passeio de Barco"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="duration">Duracao</Label>
                    <Input
                      id="duration"
                      value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                      className="border-gold/30 focus:border-gold"
                      placeholder="Ex: 1h30"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="description">Descricao Curta</Label>
                    <Input
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                      className="border-gold/30 focus:border-gold"
                      placeholder="Descricao breve da atracao"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="full_description">Descricao Completa</Label>
                    <Textarea
                      id="full_description"
                      value={formData.full_description}
                      onChange={(e) => setFormData({ ...formData, full_description: e.target.value })}
                      className="border-gold/30 focus:border-gold"
                      placeholder="Descricao detalhada da atracao"
                      rows={3}
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="highlights">Destaques (separados por vírgula)</Label>
                    <Input
                      id="highlights"
                      value={formData.highlights}
                      onChange={(e) => setFormData({ ...formData, highlights: e.target.value })}
                      className="border-gold/30 focus:border-gold"
                      placeholder="Ex: Vista panorâmica, Guia especializado, Fotos"
                    />
                  </div>

                  {/* English content */}
                  <div className="md:col-span-2 mt-2 p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
                    <div>
                      <p className="font-heading text-sm text-navy font-semibold">🇬🇧 Conteúdo em Inglês (opcional)</p>
                      <p className="text-xs text-navy/60 mt-0.5">Mostrado quando o visitante escolhe EN. Se ficar vazio, é usado o texto em português.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label htmlFor="name_en" className="text-xs">Nome (EN)</Label>
                        <Input id="name_en" value={formData.name_en} onChange={(e) => setFormData({ ...formData, name_en: e.target.value })} className="border-amber-300 bg-white" placeholder="Ex: Boat Trip at Ponta da Piedade" data-testid="attraction-name-en" />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="description_en" className="text-xs">Descrição curta (EN)</Label>
                        <Input id="description_en" value={formData.description_en} onChange={(e) => setFormData({ ...formData, description_en: e.target.value })} className="border-amber-300 bg-white" placeholder="Short description in English" data-testid="attraction-description-en" />
                      </div>
                      <div className="space-y-1 md:col-span-2">
                        <Label htmlFor="full_description_en" className="text-xs">Descrição completa (EN)</Label>
                        <Textarea id="full_description_en" value={formData.full_description_en} onChange={(e) => setFormData({ ...formData, full_description_en: e.target.value })} className="border-amber-300 bg-white" rows={3} placeholder="Full description in English" data-testid="attraction-full-description-en" />
                      </div>
                      <div className="space-y-1 md:col-span-2">
                        <Label htmlFor="highlights_en" className="text-xs">Destaques (EN, separados por vírgula)</Label>
                        <Input id="highlights_en" value={formData.highlights_en} onChange={(e) => setFormData({ ...formData, highlights_en: e.target.value })} className="border-amber-300 bg-white" placeholder="Ex: Panoramic view, Expert guide, Photos" data-testid="attraction-highlights-en" />
                      </div>
                    </div>
                  </div>

                  {/* Split Payment Configuration */}
                  <div className="md:col-span-2 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-heading text-sm text-navy font-semibold flex items-center gap-2">
                          <Wallet className="w-4 h-4 text-gold" />
                          Configuração do Split de Pagamento Stripe
                        </p>
                        <p className="text-xs text-navy/60 mt-1">Valor Total = Valor Net (parceiro) + Valor Comissão (PASS ALGARVE)</p>
                      </div>
                      <div className="flex gap-2">
                        <Button type="button" size="sm" variant="outline"
                          onClick={() => applyCommissionPercent(40)}
                          className="text-xs border-gold/40 text-navy hover:bg-gold/10">
                          Aplicar 40%
                        </Button>
                        <Button type="button" size="sm" variant="outline"
                          onClick={() => applyCommissionPercent(30)}
                          className="text-xs border-gold/40 text-navy hover:bg-gold/10">
                          30%
                        </Button>
                        <Button type="button" size="sm" variant="outline"
                          onClick={() => applyCommissionPercent(20)}
                          className="text-xs border-gold/40 text-navy hover:bg-gold/10">
                          20%
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label htmlFor="price_total" className="text-xs font-body">Valor Total (€)</Label>
                        <Input
                          id="price_total"
                          type="number" step="0.01" min="0"
                          value={formData.price_total}
                          onChange={(e) => handlePriceTotalChange(e.target.value)}
                          className="border-gold/30 focus:border-gold bg-white"
                          placeholder="30.00"
                          data-testid="attraction-price-total"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="price_net" className="text-xs font-body text-emerald-700">Valor Net (Parceiro) €</Label>
                        <Input
                          id="price_net"
                          type="number" step="0.01" min="0"
                          value={formData.price_net}
                          onChange={(e) => handleNetChange(e.target.value)}
                          className="border-emerald-300 focus:border-emerald-500 bg-white"
                          placeholder="18.00"
                          data-testid="attraction-price-net"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="price_commission" className="text-xs font-body text-gold">Comissão PASS ALGARVE €</Label>
                        <Input
                          id="price_commission"
                          type="number" step="0.01" min="0"
                          value={formData.price_commission}
                          onChange={(e) => handleCommissionChange(e.target.value)}
                          className="border-gold/50 focus:border-gold bg-white"
                          placeholder="12.00"
                          data-testid="attraction-price-commission"
                        />
                      </div>
                    </div>
                    {formData.price_total && (
                      <p className="text-xs mt-2 font-mono text-navy/70">
                        Split: Net €{parseFloat(formData.price_net || 0).toFixed(2)} + Comissão €{parseFloat(formData.price_commission || 0).toFixed(2)} = €{(parseFloat(formData.price_net || 0) + parseFloat(formData.price_commission || 0)).toFixed(2)}
                        {Math.abs(parseFloat(formData.price_total || 0) - (parseFloat(formData.price_net || 0) + parseFloat(formData.price_commission || 0))) > 0.01 && (
                          <span className="text-red-600 font-semibold ml-2">⚠ Não bate com o Total (€{parseFloat(formData.price_total || 0).toFixed(2)})</span>
                        )}
                      </p>
                    )}
                  </div>

                  {/* Partner Association */}
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="partner_id">Parceiro associado</Label>
                    <select
                      id="partner_id"
                      value={formData.partner_id}
                      onChange={(e) => setFormData({ ...formData, partner_id: e.target.value })}
                      className="w-full rounded-md border border-gold/30 bg-white px-3 py-2 text-sm focus:border-gold focus:outline-none"
                      data-testid="attraction-partner-select"
                    >
                      <option value="">— Sem parceiro —</option>
                      {partners.map((p) => (
                        <option key={p.id} value={p.id}>{p.name} ({p.email})</option>
                      ))}
                    </select>
                    <p className="text-xs text-navy/50">O Valor Net será transferido a este parceiro via Stripe Connect quando o cliente visitar a atração.</p>
                  </div>

                  {/* Agendamento Prévio */}
                  <div className="md:col-span-2 mt-2 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                    <div>
                      <p className="font-heading text-sm text-navy font-semibold">📅 Agendamento Prévio</p>
                      <p className="text-xs text-navy/60 mt-0.5">Para experiências que exigem reserva de data e horário (ex.: passeios de barco). Não altera QR Code nem validação.</p>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="requires_booking" className="text-xs">Requer agendamento prévio?</Label>
                      <select
                        id="requires_booking"
                        value={formData.requires_booking ? "yes" : "no"}
                        onChange={(e) => setFormData({ ...formData, requires_booking: e.target.value === "yes" })}
                        className="w-full rounded-md border border-blue-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                        data-testid="attraction-requires-booking-select"
                      >
                        <option value="no">Não</option>
                        <option value="yes">Sim</option>
                      </select>
                    </div>

                    {formData.requires_booking && (
                      <div className="space-y-3 pt-1" data-testid="booking-extra-fields">
                        <div className="space-y-1">
                          <Label className="text-xs">Tipo de agendamento (selecione um ou mais)</Label>
                          <div className="flex flex-wrap gap-3">
                            {BOOKING_METHOD_OPTIONS.map((opt) => (
                              <label key={opt.value} className="flex items-center gap-1.5 text-sm text-navy cursor-pointer bg-white border border-blue-200 rounded-md px-3 py-1.5">
                                <input
                                  type="checkbox"
                                  checked={formData.booking_methods.includes(opt.value)}
                                  onChange={(e) => {
                                    const methods = e.target.checked
                                      ? [...formData.booking_methods, opt.value]
                                      : formData.booking_methods.filter(m => m !== opt.value);
                                    setFormData({ ...formData, booking_methods: methods });
                                  }}
                                  className="accent-[#D4A85D] w-4 h-4"
                                  data-testid={`booking-method-${opt.value}`}
                                />
                                {opt.label}
                              </label>
                            ))}
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Nome da agência/local de agendamento</Label>
                            <Input value={formData.booking_agency_name} onChange={(e) => setFormData({ ...formData, booking_agency_name: e.target.value })} className="border-blue-300 bg-white" placeholder="Ex: SeaTrips Lagos" data-testid="booking-agency-name" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Endereço para agendamento</Label>
                            <Input value={formData.booking_address} onChange={(e) => setFormData({ ...formData, booking_address: e.target.value })} className="border-blue-300 bg-white" placeholder="Ex: Marina de Lagos, Lagos" data-testid="booking-address" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Link Google Maps</Label>
                            <Input value={formData.booking_maps_url} onChange={(e) => setFormData({ ...formData, booking_maps_url: e.target.value })} className="border-blue-300 bg-white" placeholder="https://maps.google.com/..." data-testid="booking-maps-url" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Telefone</Label>
                            <Input value={formData.booking_phone} onChange={(e) => setFormData({ ...formData, booking_phone: e.target.value })} className="border-blue-300 bg-white" placeholder="+351 282 000 000" data-testid="booking-phone" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">WhatsApp</Label>
                            <Input value={formData.booking_whatsapp} onChange={(e) => setFormData({ ...formData, booking_whatsapp: e.target.value })} className="border-blue-300 bg-white" placeholder="+351 910 000 000" data-testid="booking-whatsapp" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">E-mail</Label>
                            <Input value={formData.booking_email} onChange={(e) => setFormData({ ...formData, booking_email: e.target.value })} className="border-blue-300 bg-white" placeholder="reservas@parceiro.com" data-testid="booking-email" />
                          </div>
                          <div className="space-y-1 md:col-span-2">
                            <Label className="text-xs">Link externo para reserva</Label>
                            <Input value={formData.booking_external_url} onChange={(e) => setFormData({ ...formData, booking_external_url: e.target.value })} className="border-blue-300 bg-white" placeholder="https://..." data-testid="booking-external-url" />
                          </div>
                          <div className="space-y-1 md:col-span-2">
                            <Label className="text-xs">Instruções adicionais para agendamento</Label>
                            <Textarea value={formData.booking_instructions} onChange={(e) => setFormData({ ...formData, booking_instructions: e.target.value })} className="border-blue-300 bg-white" rows={2} placeholder="Ex: Apresente o seu PASS na receção da agência" data-testid="booking-instructions" />
                          </div>
                        </div>
                        <p className="text-[11px] text-navy/50">Nenhum campo é obrigatório — preencha apenas os meios que o parceiro utiliza. Estrutura preparada para futura integração via API (ex.: FareHarbor).</p>
                      </div>
                    )}
                  </div>

                  {/* SEO / Google */}
                  <div className="md:col-span-2 mt-2 p-4 bg-emerald-50 border border-emerald-200 rounded-lg space-y-3">
                    <div>
                      <p className="font-heading text-sm text-navy font-semibold">🔎 SEO / Google</p>
                      <p className="text-xs text-navy/60 mt-0.5">SEO independente por idioma. Se ficar vazio, o sistema gera automaticamente a partir dos dados da experiência.</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setSeoTab("pt")}
                        className={`px-4 py-1.5 rounded-md text-xs font-bold transition-colors ${seoTab === "pt" ? "bg-[#071A35] text-white" : "bg-white border border-emerald-300 text-navy/70 hover:bg-emerald-100"}`}
                        data-testid="seo-tab-pt"
                      >
                        🇵🇹 SEO Português
                      </button>
                      <button
                        type="button"
                        onClick={() => setSeoTab("en")}
                        className={`px-4 py-1.5 rounded-md text-xs font-bold transition-colors ${seoTab === "en" ? "bg-[#071A35] text-white" : "bg-white border border-emerald-300 text-navy/70 hover:bg-emerald-100"}`}
                        data-testid="seo-tab-en"
                      >
                        🇬🇧 SEO English
                      </button>
                    </div>
                    {seoTab === "pt" ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">SEO Title PT <span className={formData.seo_title.length > 60 ? "text-red-500 font-bold" : "text-navy/50"}>({formData.seo_title.length}/60 recomendado)</span></Label>
                        <Input value={formData.seo_title} onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })} className="border-emerald-300 bg-white" placeholder="Ex: Observação de Golfinhos em Lagos | Bilhetes | PASS Algarve" data-testid="attraction-seo-title" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">URL Slug PT (sem espaços/acentos)</Label>
                        <Input value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} className="border-emerald-300 bg-white" placeholder="Ex: observacao-de-golfinhos-lagos" data-testid="attraction-slug" />
                        <p className="text-[11px] text-navy/50">URL: /experiencias/{formData.slug || "(id)"}</p>
                      </div>
                      <div className="space-y-1 md:col-span-2">
                        <Label className="text-xs">Meta Description PT <span className={formData.seo_description.length > 160 ? "text-red-500 font-bold" : "text-navy/50"}>({formData.seo_description.length}/160 recomendado)</span></Label>
                        <Textarea value={formData.seo_description} onChange={(e) => setFormData({ ...formData, seo_description: e.target.value })} className="border-emerald-300 bg-white" rows={2} placeholder="Resumo atrativo para o Google" data-testid="attraction-seo-description" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Palavra-chave principal PT</Label>
                        <Input value={formData.seo_keyword} onChange={(e) => setFormData({ ...formData, seo_keyword: e.target.value })} className="border-emerald-300 bg-white" placeholder="Ex: observação de golfinhos Lagos" data-testid="attraction-seo-keyword" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Imagem para partilha social PT (URL, opcional)</Label>
                        <Input value={formData.social_image} onChange={(e) => setFormData({ ...formData, social_image: e.target.value })} className="border-emerald-300 bg-white" placeholder="Se vazio, usa a imagem principal" data-testid="attraction-social-image" />
                      </div>
                    </div>
                    ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">SEO Title EN <span className={formData.seo_title_en.length > 60 ? "text-red-500 font-bold" : "text-navy/50"}>({formData.seo_title_en.length}/60 recommended)</span></Label>
                        <Input value={formData.seo_title_en} onChange={(e) => setFormData({ ...formData, seo_title_en: e.target.value })} className="border-emerald-300 bg-white" placeholder="Ex: Dolphin Watching in Lagos | Tickets | PASS Algarve" data-testid="attraction-seo-title-en" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">URL Slug EN (no spaces/accents)</Label>
                        <Input value={formData.slug_en} onChange={(e) => setFormData({ ...formData, slug_en: e.target.value })} className="border-emerald-300 bg-white" placeholder="Ex: dolphin-watching-lagos" data-testid="attraction-slug-en" />
                        <p className="text-[11px] text-navy/50">URL: /en/experiences/{formData.slug_en || formData.slug || "(id)"}</p>
                      </div>
                      <div className="space-y-1 md:col-span-2">
                        <Label className="text-xs">Meta Description EN <span className={formData.seo_description_en.length > 160 ? "text-red-500 font-bold" : "text-navy/50"}>({formData.seo_description_en.length}/160 recommended)</span></Label>
                        <Textarea value={formData.seo_description_en} onChange={(e) => setFormData({ ...formData, seo_description_en: e.target.value })} className="border-emerald-300 bg-white" rows={2} placeholder="Attractive summary for Google" data-testid="attraction-seo-description-en" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Main Keyword EN</Label>
                        <Input value={formData.seo_keyword_en} onChange={(e) => setFormData({ ...formData, seo_keyword_en: e.target.value })} className="border-emerald-300 bg-white" placeholder="Ex: dolphin watching Lagos" data-testid="attraction-seo-keyword-en" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Social Share Image EN (URL, optional)</Label>
                        <Input value={formData.social_image_en} onChange={(e) => setFormData({ ...formData, social_image_en: e.target.value })} className="border-emerald-300 bg-white" placeholder="If empty, uses the PT image or main image" data-testid="attraction-social-image-en" />
                      </div>
                    </div>
                    )}
                    <div className="pt-2 border-t border-emerald-200">
                      <div className="space-y-1 max-w-xs">
                        <Label className="text-xs">Permitir indexação no Google? (global)</Label>
                        <select
                          value={formData.indexable ? "yes" : "no"}
                          onChange={(e) => setFormData({ ...formData, indexable: e.target.value === "yes" })}
                          className="w-full rounded-md border border-emerald-300 bg-white px-3 py-2 text-sm focus:outline-none"
                          data-testid="attraction-indexable"
                        >
                          <option value="yes">Sim</option>
                          <option value="no">Não</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex gap-4 pt-4">
                  <Button type="submit" disabled={uploading} className="bg-gold hover:bg-gold-dark text-white">
                    {editingAttraction ? "Atualizar" : "Criar Atracao"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => {
                    setShowForm(false);
                    setEditingAttraction(null);
                  }}>
                    Cancelar
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      )}


      {/* Attractions List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {attractions.map((attraction) => {
          const mainImage = attraction.images?.[0] || attraction.image_url;
          const imageCount = attraction.images?.length || (attraction.image_url ? 1 : 0);
          const isActive = attraction.is_active !== false; // Default to true if not set
          return (
            <Card key={attraction.id} className="bg-white border-gold/20 shadow-lg overflow-hidden">
              <div className="relative h-40">
                <img 
                  src={mainImage?.startsWith('/api') ? `${API.replace('/api', '')}${mainImage}` : mainImage} 
                  alt={attraction.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute top-3 right-3">
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${isActive ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}>
                    {isActive ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
                <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                  <h3 className="font-heading text-lg text-white">{attraction.name}</h3>
                  {imageCount > 1 && (
                    <span className="bg-white/20 backdrop-blur text-white text-xs px-2 py-1 rounded">
                      {imageCount} fotos
                    </span>
                  )}
                </div>
              </div>
              <CardContent className="pt-4">
                <p className="text-navy/70 text-sm mb-3">{attraction.description}</p>
                {(() => {
                  const partner = partners.find(p => p.id === attraction.partner_id);
                  const partnerName = partner?.name || partner?.email || null;
                  return partnerName ? (
                    <div
                      className="mb-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-navy/5 border border-navy/10"
                      data-testid={`attraction-partner-${attraction.id}`}
                    >
                      <UserPlus className="h-3 w-3 text-navy/60" />
                      <span className="text-[11px] font-medium text-navy/80">
                        Parceiro: <span className="text-navy font-semibold">{partnerName}</span>
                      </span>
                    </div>
                  ) : (
                    <div
                      className="mb-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200"
                      data-testid={`attraction-no-partner-${attraction.id}`}
                    >
                      <UserPlus className="h-3 w-3 text-amber-700" />
                      <span className="text-[11px] font-medium text-amber-700">
                        Sem parceiro atribuído
                      </span>
                    </div>
                  );
                })()}
                {(attraction.price_total || attraction.price_net || attraction.price_commission) ? (
                  <div className="grid grid-cols-3 gap-2 mb-3 p-2 bg-gray-50 rounded-lg">
                    <div className="text-center">
                      <p className="text-navy/50 text-[10px] uppercase tracking-wider">Total</p>
                      <p className="font-heading text-sm text-navy font-semibold">€{(attraction.price_total || 0).toFixed(2)}</p>
                    </div>
                    <div className="text-center border-l border-gray-200">
                      <p className="text-emerald-700/70 text-[10px] uppercase tracking-wider">Net</p>
                      <p className="font-heading text-sm text-emerald-700 font-semibold">€{(attraction.price_net || 0).toFixed(2)}</p>
                    </div>
                    <div className="text-center border-l border-gray-200">
                      <p className="text-gold text-[10px] uppercase tracking-wider">Comissão</p>
                      <p className="font-heading text-sm text-gold font-semibold">€{(attraction.price_commission || 0).toFixed(2)}</p>
                    </div>
                  </div>
                ) : (
                  <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-lg text-center">
                    <p className="text-xs text-amber-700">⚠ Valores de split não configurados</p>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-navy/50 text-xs">Duração: {attraction.duration || "N/A"}</span>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={(e) => handleToggleActive(e, attraction)}
                      className={isActive 
                        ? "text-emerald-600 border-emerald-600 hover:bg-emerald-50" 
                        : "text-orange-600 border-orange-600 hover:bg-orange-50"
                      }
                      data-testid={`toggle-attraction-${attraction.id}`}
                    >
                      <Power className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={(e) => handleEdit(e, attraction)}
                      className="text-blue-600 border-blue-600 hover:bg-blue-50"
                      data-testid={`edit-attraction-${attraction.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={(e) => handleDelete(e, attraction.id)}
                      className="text-red-600 border-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

// Passport Types Management
const EMPTY_PASSPORT_TYPE_FORM = {
  name: "",
  description: "",
  price: "",
  validity_days: 365,
  attractions: [],
  image_url: "",
  name_en: "",
  description_en: "",
  seo_title: "",
  seo_description: "",
  seo_keyword: "",
  slug: "",
  social_image: "",
  seo_title_en: "",
  seo_description_en: "",
  seo_keyword_en: "",
  slug_en: "",
  social_image_en: "",
  indexable: true
};

const PassportTypesManagement = () => {
  const { token, API } = useAuth();
  const [passportTypes, setPassportTypes] = useState([]);
  const [attractions, setAttractions] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [seoTab, setSeoTab] = useState("pt");
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_PASSPORT_TYPE_FORM });
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [typesRes, attractionsRes, partnersRes] = await Promise.all([
        axios.get(`${API}/admin/passport-types`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/attractions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/partners`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setPassportTypes(typesRes.data);
      setAttractions(attractionsRes.data);
      setPartners(partnersRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      slug: (formData.slug || "").trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, ""),
      slug_en: (formData.slug_en || "").trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, ""),
      price: parseFloat(formData.price)
    };

    try {
      if (editingType) {
        await axios.put(`${API}/admin/passport-types/${editingType.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Tipo de passaporte atualizado");
      } else {
        await axios.post(`${API}/admin/passport-types`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Tipo de passaporte criado");
      }
      
      setShowForm(false);
      setEditingType(null);
      setFormData({ ...EMPTY_PASSPORT_TYPE_FORM });
      fetchData();
    } catch (error) {
      console.error("Error saving passport type:", error);
      toast.error(error.response?.data?.detail || "Erro ao salvar");
    }
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      description: type.description,
      price: type.price.toString(),
      validity_days: type.validity_days,
      attractions: type.attractions || [],
      image_url: type.image_url || "",
      name_en: type.name_en || "",
      description_en: type.description_en || "",
      seo_title: type.seo_title || "",
      seo_description: type.seo_description || "",
      seo_keyword: type.seo_keyword || "",
      slug: type.slug || "",
      social_image: type.social_image || "",
      seo_title_en: type.seo_title_en || "",
      seo_description_en: type.seo_description_en || "",
      seo_keyword_en: type.seo_keyword_en || "",
      slug_en: type.slug_en || "",
      social_image_en: type.social_image_en || "",
      indexable: type.indexable !== false
    });
    setShowForm(true);
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Imagem demasiado grande (máx 5MB)");
      return;
    }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/upload-image`, fd, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const url = res.data?.url;
      if (url) {
        setFormData(prev => ({ ...prev, image_url: url }));
        toast.success("Imagem carregada com sucesso");
      } else {
        toast.error("Erro ao processar imagem");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Erro ao carregar imagem");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDelete = async (typeId) => {
    if (!window.confirm("Tem certeza que deseja remover este tipo de passaporte?")) return;
    
    try {
      await axios.delete(`${API}/admin/passport-types/${typeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Tipo de passaporte removido");
      fetchData();
    } catch (error) {
      console.error("Error deleting passport type:", error);
      toast.error("Erro ao remover");
    }
  };

  const handleToggleActive = async (type) => {
    try {
      await axios.put(`${API}/admin/passport-types/${type.id}`, {
        ...type,
        is_active: !type.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(type.is_active ? "Passaporte desativado" : "Passaporte ativado");
      fetchData();
    } catch (error) {
      console.error("Error toggling passport status:", error);
      toast.error("Erro ao alterar status");
    }
  };

  const toggleAttraction = (attractionId) => {
    setFormData(prev => ({
      ...prev,
      attractions: prev.attractions.includes(attractionId)
        ? prev.attractions.filter(id => id !== attractionId)
        : [...prev.attractions, attractionId]
    }));
  };

  // Live breakdown computed from currently-selected attractions
  const selectedAttrs = attractions.filter(a => formData.attractions.includes(a.id));
  const sumTotal = selectedAttrs.reduce((s, a) => s + (parseFloat(a.price_total) || 0), 0);
  const sumNet = selectedAttrs.reduce((s, a) => s + (parseFloat(a.price_net) || 0), 0);
  const sumCommission = selectedAttrs.reduce((s, a) => s + (parseFloat(a.price_commission) || 0), 0);
  const currentPrice = parseFloat(formData.price) || 0;
  const discount = Math.max(0, sumTotal - currentPrice);
  const discountPct = sumTotal > 0 ? (discount / sumTotal * 100) : 0;
  const platformMargin = currentPrice - sumNet;
  const marginPct = currentPrice > 0 ? (platformMargin / currentPrice * 100) : 0;

  let marginStatus = "neutral";
  let marginMsg = "Preencha preço e atrações para ver impacto financeiro";
  if (sumNet > 0 && currentPrice > 0) {
    if (currentPrice < sumNet) {
      marginStatus = "danger";
      marginMsg = `Preço (€${currentPrice.toFixed(2)}) abaixo da soma dos Nets (€${sumNet.toFixed(2)}) — plataforma perde €${(sumNet - currentPrice).toFixed(2)} por venda!`;
    } else if (marginPct < 10) {
      marginStatus = "warn";
      marginMsg = `Margem baixa (${marginPct.toFixed(1)}%) — considere aumentar o preço`;
    } else {
      marginStatus = "ok";
      marginMsg = `Margem saudável: €${platformMargin.toFixed(2)} (${marginPct.toFixed(1)}%)`;
    }
  } else if (selectedAttrs.length > 0 && sumNet === 0) {
    marginStatus = "warn";
    marginMsg = "As atrações selecionadas não têm preço Net configurado. Edite cada atração para definir os splits Total/Net/Comissão antes de garantir uma margem realista.";
  } else if (currentPrice > 0 && selectedAttrs.length === 0) {
    marginStatus = "warn";
    marginMsg = "Selecione pelo menos uma atração para o passaporte";
  }

  const applySuggestedPrice = (percentOff) => {
    if (sumTotal <= 0) {
      toast.error("Selecione atrações primeiro");
      return;
    }
    const newPrice = +(sumTotal * (1 - percentOff / 100)).toFixed(2);
    setFormData({ ...formData, price: String(newPrice) });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl text-navy mb-2">Tipos de Passaporte</h1>
          <p className="text-navy/60 font-body">Crie diferentes pacotes de passaporte</p>
        </div>
        <Button 
          onClick={() => {
            setEditingType(null);
            setFormData({ ...EMPTY_PASSPORT_TYPE_FORM });
            setShowForm(!showForm);
          }}
          className="bg-gold hover:bg-gold-dark text-white rounded-full"
          data-testid="add-passport-type-btn"
        >
          <Plus className="mr-2 h-4 w-4" />
          Novo Tipo
        </Button>
      </div>

      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="bg-white border-gold/20 shadow-lg">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-navy">
                {editingType ? "Editar Tipo de Passaporte" : "Novo Tipo de Passaporte"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nome do Passaporte</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="border-gold/30 focus:border-gold"
                      placeholder="Ex: PASS ALGARVE Premium"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="price">Preco (EUR)</Label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      required
                      className="border-gold/30 focus:border-gold"
                      placeholder="99.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="validity_days">Validade (dias)</Label>
                    <Input
                      id="validity_days"
                      type="number"
                      value={formData.validity_days}
                      onChange={(e) => setFormData({ ...formData, validity_days: parseInt(e.target.value) })}
                      required
                      className="border-gold/30 focus:border-gold"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Descricao</Label>
                    <Input
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                      className="border-gold/30 focus:border-gold"
                      placeholder="Descricao do passaporte"
                    />
                  </div>
                  <div className="md:col-span-2 p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
                    <div>
                      <p className="font-heading text-sm text-navy font-semibold">🇬🇧 Conteúdo em Inglês (opcional)</p>
                      <p className="text-xs text-navy/60 mt-0.5">Mostrado quando o visitante escolhe EN. Se ficar vazio, é usado o texto em português.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label htmlFor="pt_name_en" className="text-xs">Nome (EN)</Label>
                        <Input id="pt_name_en" value={formData.name_en} onChange={(e) => setFormData({ ...formData, name_en: e.target.value })} className="border-amber-300 bg-white" placeholder="Ex: PASS ALGARVE Premium" data-testid="type-name-en" />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="pt_description_en" className="text-xs">Descrição (EN)</Label>
                        <Input id="pt_description_en" value={formData.description_en} onChange={(e) => setFormData({ ...formData, description_en: e.target.value })} className="border-amber-300 bg-white" placeholder="Passport description in English" data-testid="type-description-en" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* === Image upload === */}
                <div className="space-y-2">
                  <Label>Imagem do Passaporte</Label>
                  <div className="flex flex-col sm:flex-row gap-4 items-start">
                    {formData.image_url && (
                      <div className="relative w-40 h-28 rounded-lg overflow-hidden border-2 border-gold/30 flex-shrink-0">
                        <img src={formData.image_url} alt="Preview" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, image_url: "" }))}
                          className="absolute top-1 right-1 bg-rose-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-rose-600"
                          title="Remover imagem"
                        >
                          ×
                        </button>
                      </div>
                    )}
                    <div className="flex-1 space-y-2">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e.target.files?.[0])}
                        disabled={uploadingImage}
                        className="border-gold/30 focus:border-gold cursor-pointer"
                        data-testid="passport-image-file"
                      />
                      <Input
                        type="text"
                        placeholder="ou cole uma URL: https://..."
                        value={formData.image_url}
                        onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                        className="border-gold/30 focus:border-gold text-xs"
                        data-testid="passport-image-url"
                      />
                      {uploadingImage && <p className="text-xs text-gold">A carregar imagem…</p>}
                      <p className="text-[11px] text-navy/50">
                        Esta imagem aparece na home e na página interna do passaporte. Recomendado: 1200×800px, JPG/PNG, máx 5MB.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Atrações Incluídas</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {attractions.map((attraction) => {
                      const selected = formData.attractions.includes(attraction.id);
                      const pt = parseFloat(attraction.price_total) || 0;
                      const pn = parseFloat(attraction.price_net) || 0;
                      const partner = partners.find(p => p.id === attraction.partner_id);
                      const partnerName = partner?.name || partner?.email || null;
                      return (
                        <button
                          key={attraction.id}
                          type="button"
                          onClick={() => toggleAttraction(attraction.id)}
                          data-testid={`attr-toggle-${attraction.id}`}
                          className={`p-3 rounded-lg border text-left text-sm transition-all ${
                            selected
                              ? 'bg-gold/10 border-gold text-navy'
                              : 'bg-gray-50 border-gray-200 text-navy/60 hover:border-gold/50'
                          }`}
                        >
                          <div className="font-semibold">{attraction.name}</div>
                          <div
                            className={`text-[11px] mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
                              partnerName
                                ? 'bg-navy/5 text-navy/70'
                                : 'bg-amber-50 text-amber-700'
                            }`}
                            data-testid={`attr-partner-${attraction.id}`}
                          >
                            <Users className="h-3 w-3" />
                            {partnerName || 'Sem parceiro atribuído'}
                          </div>
                          <div className="text-xs mt-1 opacity-80">
                            Total €{pt.toFixed(2)} · Net €{pn.toFixed(2)}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* === Live Financial Breakdown (Hybrid Pricing Model) === */}
                <div
                  className="rounded-xl border-2 border-gold/30 bg-gradient-to-br from-gold/5 to-amber-50/40 p-4 space-y-3"
                  data-testid="pricing-breakdown-panel"
                >
                  <div className="flex items-center justify-between">
                    <h4 className="font-heading text-base text-navy">Análise Financeira ao Vivo</h4>
                    <span className="text-xs text-navy/50 font-mono">
                      {selectedAttrs.length} {selectedAttrs.length === 1 ? "atração" : "atrações"} selecionada(s)
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-white rounded-lg p-3 border border-gold/20">
                      <div className="text-xs text-navy/60 mb-1">Soma Totais</div>
                      <div className="font-heading text-lg text-navy" data-testid="sum-total">€{sumTotal.toFixed(2)}</div>
                      <div className="text-[10px] text-navy/40">Valor "cheio" às atrações</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-gold/20">
                      <div className="text-xs text-navy/60 mb-1">Soma Nets</div>
                      <div className="font-heading text-lg text-rose-600" data-testid="sum-net">€{sumNet.toFixed(2)}</div>
                      <div className="text-[10px] text-navy/40">Pago aos parceiros</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-gold/20">
                      <div className="text-xs text-navy/60 mb-1">Preço Passaporte</div>
                      <div className="font-heading text-lg text-gold" data-testid="current-price">€{currentPrice.toFixed(2)}</div>
                      <div className="text-[10px] text-navy/40">O que o cliente paga</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-gold/20">
                      <div className="text-xs text-navy/60 mb-1">Margem Plataforma</div>
                      <div
                        className={`font-heading text-lg ${
                          marginStatus === "danger" ? "text-red-600" :
                          marginStatus === "warn" ? "text-amber-600" :
                          marginStatus === "ok" ? "text-emerald-600" : "text-navy/40"
                        }`}
                        data-testid="platform-margin"
                      >
                        €{platformMargin.toFixed(2)}
                      </div>
                      <div className="text-[10px] text-navy/40">{marginPct.toFixed(1)}% sobre o preço</div>
                    </div>
                  </div>

                  {/* Discount info */}
                  {sumTotal > 0 && currentPrice > 0 && currentPrice < sumTotal && (
                    <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                      <span className="text-sm text-emerald-800">
                        Desconto exibido ao cliente: <strong>{discountPct.toFixed(0)}%</strong> off
                        (poupa <strong>€{discount.toFixed(2)}</strong> vs comprar separadamente)
                      </span>
                    </div>
                  )}

                  {/* Margin status banner */}
                  <div
                    className={`rounded-lg px-3 py-2 text-sm border ${
                      marginStatus === "danger" ? "bg-red-50 border-red-300 text-red-800" :
                      marginStatus === "warn" ? "bg-amber-50 border-amber-300 text-amber-800" :
                      marginStatus === "ok" ? "bg-emerald-50 border-emerald-300 text-emerald-800" :
                      "bg-gray-50 border-gray-200 text-navy/60"
                    }`}
                    data-testid="margin-status-banner"
                  >
                    {marginMsg}
                  </div>

                  {/* Quick price suggestions */}
                  {sumTotal > 0 && (
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <span className="text-xs text-navy/60">Sugestões de preço:</span>
                      <button
                        type="button"
                        onClick={() => applySuggestedPrice(0)}
                        className="text-xs px-2 py-1 rounded-md border border-gold/30 hover:bg-gold/10 text-navy"
                        data-testid="suggest-price-0"
                      >
                        100% (€{sumTotal.toFixed(2)})
                      </button>
                      <button
                        type="button"
                        onClick={() => applySuggestedPrice(10)}
                        className="text-xs px-2 py-1 rounded-md border border-gold/30 hover:bg-gold/10 text-navy"
                        data-testid="suggest-price-10"
                      >
                        -10% (€{(sumTotal * 0.9).toFixed(2)})
                      </button>
                      <button
                        type="button"
                        onClick={() => applySuggestedPrice(20)}
                        className="text-xs px-2 py-1 rounded-md border border-gold/30 hover:bg-gold/10 text-navy"
                        data-testid="suggest-price-20"
                      >
                        -20% (€{(sumTotal * 0.8).toFixed(2)})
                      </button>
                      <button
                        type="button"
                        onClick={() => applySuggestedPrice(30)}
                        className="text-xs px-2 py-1 rounded-md border border-gold/30 hover:bg-gold/10 text-navy"
                        data-testid="suggest-price-30"
                      >
                        -30% (€{(sumTotal * 0.7).toFixed(2)})
                      </button>
                    </div>
                  )}
                </div>

                {/* SEO / Google */}
                <div className="mt-2 p-4 bg-emerald-50 border border-emerald-200 rounded-lg space-y-3">
                  <div>
                    <p className="font-heading text-sm text-navy font-semibold">🔎 SEO / Google</p>
                    <p className="text-xs text-navy/60 mt-0.5">SEO independente por idioma. Se ficar vazio, o sistema gera automaticamente a partir dos dados do passaporte.</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setSeoTab("pt")}
                      className={`px-4 py-1.5 rounded-md text-xs font-bold transition-colors ${seoTab === "pt" ? "bg-[#071A35] text-white" : "bg-white border border-emerald-300 text-navy/70 hover:bg-emerald-100"}`}
                      data-testid="pt-seo-tab-pt"
                    >
                      🇵🇹 SEO Português
                    </button>
                    <button
                      type="button"
                      onClick={() => setSeoTab("en")}
                      className={`px-4 py-1.5 rounded-md text-xs font-bold transition-colors ${seoTab === "en" ? "bg-[#071A35] text-white" : "bg-white border border-emerald-300 text-navy/70 hover:bg-emerald-100"}`}
                      data-testid="pt-seo-tab-en"
                    >
                      🇬🇧 SEO English
                    </button>
                  </div>
                  {seoTab === "pt" ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">SEO Title PT <span className={formData.seo_title.length > 60 ? "text-red-500 font-bold" : "text-navy/50"}>({formData.seo_title.length}/60 recomendado)</span></Label>
                      <Input value={formData.seo_title} onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })} className="border-emerald-300 bg-white" placeholder="Ex: PASS Premium | Passe de Experiências no Algarve" data-testid="passport-seo-title" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">URL Slug PT (sem espaços/acentos)</Label>
                      <Input value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} className="border-emerald-300 bg-white" placeholder="Ex: passe-premium-algarve" data-testid="passport-slug" />
                      <p className="text-[11px] text-navy/50">URL: /passaporte/{formData.slug || "(id)"}</p>
                    </div>
                    <div className="space-y-1 md:col-span-2">
                      <Label className="text-xs">Meta Description PT <span className={formData.seo_description.length > 160 ? "text-red-500 font-bold" : "text-navy/50"}>({formData.seo_description.length}/160 recomendado)</span></Label>
                      <Textarea value={formData.seo_description} onChange={(e) => setFormData({ ...formData, seo_description: e.target.value })} className="border-emerald-300 bg-white" rows={2} placeholder="Resumo atrativo para o Google" data-testid="passport-seo-description" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Palavra-chave principal PT</Label>
                      <Input value={formData.seo_keyword} onChange={(e) => setFormData({ ...formData, seo_keyword: e.target.value })} className="border-emerald-300 bg-white" placeholder="Ex: passe de atrações Algarve" data-testid="passport-seo-keyword" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Imagem para partilha social PT (URL, opcional)</Label>
                      <Input value={formData.social_image} onChange={(e) => setFormData({ ...formData, social_image: e.target.value })} className="border-emerald-300 bg-white" placeholder="Se vazio, usa a imagem principal" data-testid="passport-social-image" />
                    </div>
                  </div>
                  ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">SEO Title EN <span className={formData.seo_title_en.length > 60 ? "text-red-500 font-bold" : "text-navy/50"}>({formData.seo_title_en.length}/60 recommended)</span></Label>
                      <Input value={formData.seo_title_en} onChange={(e) => setFormData({ ...formData, seo_title_en: e.target.value })} className="border-emerald-300 bg-white" placeholder="Ex: PASS Premium | Algarve Experiences Pass" data-testid="passport-seo-title-en" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">URL Slug EN (no spaces/accents)</Label>
                      <Input value={formData.slug_en} onChange={(e) => setFormData({ ...formData, slug_en: e.target.value })} className="border-emerald-300 bg-white" placeholder="Ex: premium-algarve-pass" data-testid="passport-slug-en" />
                      <p className="text-[11px] text-navy/50">URL: /en/passport/{formData.slug_en || formData.slug || "(id)"}</p>
                    </div>
                    <div className="space-y-1 md:col-span-2">
                      <Label className="text-xs">Meta Description EN <span className={formData.seo_description_en.length > 160 ? "text-red-500 font-bold" : "text-navy/50"}>({formData.seo_description_en.length}/160 recommended)</span></Label>
                      <Textarea value={formData.seo_description_en} onChange={(e) => setFormData({ ...formData, seo_description_en: e.target.value })} className="border-emerald-300 bg-white" rows={2} placeholder="Attractive summary for Google" data-testid="passport-seo-description-en" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Main Keyword EN</Label>
                      <Input value={formData.seo_keyword_en} onChange={(e) => setFormData({ ...formData, seo_keyword_en: e.target.value })} className="border-emerald-300 bg-white" placeholder="Ex: Algarve attractions pass" data-testid="passport-seo-keyword-en" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Social Share Image EN (URL, optional)</Label>
                      <Input value={formData.social_image_en} onChange={(e) => setFormData({ ...formData, social_image_en: e.target.value })} className="border-emerald-300 bg-white" placeholder="If empty, uses the PT image or main image" data-testid="passport-social-image-en" />
                    </div>
                  </div>
                  )}
                  <div className="pt-2 border-t border-emerald-200">
                    <div className="space-y-1 max-w-xs">
                      <Label className="text-xs">Permitir indexação no Google? (global)</Label>
                      <select
                        value={formData.indexable ? "yes" : "no"}
                        onChange={(e) => setFormData({ ...formData, indexable: e.target.value === "yes" })}
                        className="w-full rounded-md border border-emerald-300 bg-white px-3 py-2 text-sm focus:outline-none"
                        data-testid="passport-indexable"
                      >
                        <option value="yes">Sim</option>
                        <option value="no">Não</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <Button
                    type="submit"
                    disabled={marginStatus === "danger" && (
                      !editingType ||
                      parseFloat(formData.price) !== parseFloat(editingType.price) ||
                      JSON.stringify(formData.attractions || []) !== JSON.stringify(editingType.attractions || [])
                    )}
                    className="bg-gold hover:bg-gold-dark text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-passport-type-btn"
                  >
                    {editingType ? "Atualizar" : "Criar Passaporte"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => {
                    setShowForm(false);
                    setEditingType(null);
                  }}>
                    Cancelar
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Passport Types List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {passportTypes.map((type) => (
          <Card key={type.id} className="bg-white border-gold/20 shadow-lg">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg text-navy">{type.name}</CardTitle>
                <Badge className={type.is_active ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>
                  {type.is_active ? "Ativo" : "Inativo"}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-navy/70 text-sm mb-3">{type.description}</p>
              <div className="flex items-baseline gap-2 mb-3">
                <span className="font-heading text-3xl font-bold text-gold">{type.price}</span>
                <span className="text-navy/50 text-sm">EUR</span>
                {type.discount_amount > 0 && (
                  <span className="text-xs text-navy/40 line-through ml-1">
                    €{type.sum_total?.toFixed(2)}
                  </span>
                )}
                {type.discount_percent > 0 && (
                  <Badge className="bg-emerald-100 text-emerald-700 ml-1 text-[10px]">
                    -{type.discount_percent.toFixed(0)}%
                  </Badge>
                )}
              </div>

              {/* Financial breakdown summary */}
              {(type.sum_total > 0 || type.sum_net > 0) && (
                <div className="grid grid-cols-3 gap-1 mb-3 text-[11px]">
                  <div className="bg-gray-50 rounded p-1.5 text-center">
                    <div className="text-navy/50">Net</div>
                    <div className="font-semibold text-rose-600">€{type.sum_net?.toFixed(2)}</div>
                  </div>
                  <div className="bg-gray-50 rounded p-1.5 text-center">
                    <div className="text-navy/50">Margem</div>
                    <div className={`font-semibold ${
                      type.platform_margin < 0 ? "text-red-600" :
                      type.platform_margin < (type.price * 0.1) ? "text-amber-600" :
                      "text-emerald-600"
                    }`}>
                      €{type.platform_margin?.toFixed(2)}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded p-1.5 text-center">
                    <div className="text-navy/50">Atrações</div>
                    <div className="font-semibold text-navy">{type.attractions?.length || 0}</div>
                  </div>
                </div>
              )}

              {/* Partners involved in this passport type */}
              {(() => {
                const partnerIds = Array.from(new Set(
                  (type.attractions || [])
                    .map(aid => attractions.find(a => a.id === aid)?.partner_id)
                    .filter(Boolean)
                ));
                const involvedPartners = partnerIds
                  .map(pid => partners.find(p => p.id === pid))
                  .filter(Boolean);
                if (!type.attractions?.length) return null;
                return (
                  <div className="mb-3" data-testid={`pt-partners-${type.id}`}>
                    <div className="text-[11px] uppercase tracking-wide text-navy/40 mb-1.5 flex items-center gap-1">
                      <Users className="h-3 w-3" /> Parceiros incluídos
                    </div>
                    {involvedPartners.length === 0 ? (
                      <p className="text-[11px] text-amber-700 italic">
                        Nenhuma atração tem parceiro atribuído
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {involvedPartners.map(p => (
                          <Badge
                            key={p.id}
                            variant="outline"
                            className="text-[10px] bg-navy/5 border-navy/10 text-navy/80 font-normal"
                          >
                            {p.name || p.email}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="mb-4">
                <p className="text-navy/50 text-xs">Validade: {type.validity_days} dias</p>
              </div>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => handleToggleActive(type)}
                  className={type.is_active 
                    ? "flex-1 text-emerald-600 border-emerald-600 hover:bg-emerald-50" 
                    : "flex-1 text-orange-600 border-orange-600 hover:bg-orange-50"
                  }
                  data-testid={`toggle-passport-${type.id}`}
                >
                  <Power className="h-4 w-4 mr-1" /> 
                  {type.is_active ? "Desativar" : "Ativar"}
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => handleEdit(type)}
                  className="text-blue-600 border-blue-600 hover:bg-blue-50"
                  data-testid={`edit-passport-${type.id}`}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => handleDelete(type.id)}
                  className="text-red-600 border-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Main Admin Dashboard
// =====================================================
// TransactionsManagement — Lista de transações Stripe
// =====================================================
const TransactionsManagement = () => {
  const { token, API } = useAuth();
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 20 });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = { page, limit: 20 };
        if (status) params.status = status;
        const resp = await axios.get(`${API}/admin/transactions`, {
          headers: { Authorization: `Bearer ${token}` },
          params
        });
        setData(resp.data);
      } catch (e) {
        toast.error("Erro ao carregar transações");
      } finally {
        setLoading(false);
      }
    })();
  }, [API, token, status, page]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / (data.limit || 20)));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl text-navy mb-2">Transações</h1>
        <p className="text-navy/60 font-body">Histórico de pagamentos via Stripe</p>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className="rounded-md border border-gold/30 bg-white px-3 py-2 text-sm focus:border-gold focus:outline-none"
          data-testid="tx-filter-status"
        >
          <option value="">Todos os estados</option>
          <option value="paid">Pagos</option>
          <option value="initiated">Iniciados</option>
          <option value="expired">Expirados</option>
        </select>
        <span className="text-sm text-navy/60 font-body">{data.total} transações</span>
      </div>

      <Card className="bg-white border-gold/20 shadow-lg overflow-hidden">
        {loading ? (
          <div className="p-12 flex justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-gold"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Valor</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Session ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data.items || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-navy/50">
                      Nenhuma transação encontrada
                    </TableCell>
                  </TableRow>
                ) : (
                  data.items.map((t) => (
                    <TableRow key={t.id} data-testid={`tx-row-${t.id}`}>
                      <TableCell className="text-xs font-mono">{t.created_at?.substring(0, 16).replace("T", " ")}</TableCell>
                      <TableCell className="text-sm">{t.user_email}</TableCell>
                      <TableCell className="text-sm">{t.passport_type_name || "—"}</TableCell>
                      <TableCell className="font-semibold">€ {Number(t.amount || 0).toFixed(2)}</TableCell>
                      <TableCell>
                        <Badge className={t.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                          {t.payment_status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs font-mono text-navy/50 truncate max-w-[160px]">{t.session_id}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="p-4 flex items-center justify-between border-t border-gray-100">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="tx-prev">
              Anterior
            </Button>
            <span className="text-sm text-navy/60">Página {page} de {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="tx-next">
              Próxima
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

// =====================================================
// PassportsSoldManagement — Passaportes emitidos
// =====================================================
const PassportsSoldManagement = () => {
  const { token, API } = useAuth();
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 20 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(h);
  }, [search]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = { page, limit: 20 };
        if (debouncedSearch) params.search = debouncedSearch;
        const resp = await axios.get(`${API}/admin/passports-sold`, {
          headers: { Authorization: `Bearer ${token}` },
          params
        });
        setData(resp.data);
      } catch (e) {
        toast.error("Erro ao carregar passaportes");
      } finally {
        setLoading(false);
      }
    })();
  }, [API, token, debouncedSearch, page]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / (data.limit || 20)));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl text-navy mb-2">Passaportes Vendidos</h1>
        <p className="text-navy/60 font-body">Todos os passaportes emitidos e seus QR Codes</p>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/40" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Buscar por QR, email ou nome..."
            className="pl-10 border-gold/30"
            data-testid="passports-search"
          />
        </div>
        <span className="text-sm text-navy/60 font-body">{data.total} passaportes</span>
      </div>

      <Card className="bg-white border-gold/20 shadow-lg overflow-hidden">
        {loading ? (
          <div className="p-12 flex justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-gold"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>QR Code</TableHead>
                  <TableHead>Titular</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Compra</TableHead>
                  <TableHead>Validade</TableHead>
                  <TableHead>Visitas</TableHead>
                  <TableHead>Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data.items || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-navy/50">
                      Nenhum passaporte encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  data.items.map((p) => {
                    const expired = new Date(p.expiry_date) < new Date();
                    return (
                      <TableRow key={p.id} data-testid={`passport-row-${p.id}`}>
                        <TableCell className="font-mono text-xs font-bold text-gold">{p.qr_code}</TableCell>
                        <TableCell className="text-sm">{p.user_name}</TableCell>
                        <TableCell className="text-sm text-navy/70">{p.user_email}</TableCell>
                        <TableCell className="text-sm">{p.passport_type_name || "—"}</TableCell>
                        <TableCell className="text-xs font-mono">{p.purchase_date?.substring(0, 10)}</TableCell>
                        <TableCell className="text-xs font-mono">{p.expiry_date?.substring(0, 10)}</TableCell>
                        <TableCell className="text-sm">{p.visits?.length || 0}/4</TableCell>
                        <TableCell>
                          <Badge className={expired ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}>
                            {expired ? "Expirado" : "Ativo"}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="p-4 flex items-center justify-between border-t border-gray-100">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="passports-prev">
              Anterior
            </Button>
            <span className="text-sm text-navy/60">Página {page} de {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="passports-next">
              Próxima
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

// =====================================================
// FinancialManagement — Relatório financeiro
// =====================================================
const FinancialManagement = () => {
  const { token, API } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const resp = await axios.get(`${API}/admin/financial`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(resp.data);
      } catch (e) {
        toast.error("Erro ao carregar relatório financeiro");
      } finally {
        setLoading(false);
      }
    })();
  }, [API, token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }

  const fmt = (v) => `€ ${Number(v || 0).toFixed(2)}`;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl text-navy mb-2">Financeiro</h1>
        <p className="text-navy/60 font-body">Breakdown de receita, comissão e valores a pagar aos parceiros</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-white border-gold/20 shadow-lg">
          <CardContent className="pt-6">
            <p className="text-navy/60 text-sm font-body">Receita Total (bruta)</p>
            <p className="font-heading text-3xl text-navy mt-1">{fmt(data?.total_revenue)}</p>
            <p className="text-xs text-navy/40 mt-2">Total cobrado aos clientes</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-gold/20 shadow-lg">
          <CardContent className="pt-6">
            <p className="text-gold text-sm font-body">Comissão Acumulada</p>
            <p className="font-heading text-3xl text-gold mt-1">{fmt(data?.commission_total)}</p>
            <p className="text-xs text-navy/40 mt-2">Baseado em visitas confirmadas</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-gold/20 shadow-lg">
          <CardContent className="pt-6">
            <p className="text-emerald-700 text-sm font-body">A Pagar a Parceiros</p>
            <p className="font-heading text-3xl text-emerald-700 mt-1">{fmt(data?.partner_payable_total)}</p>
            <p className="text-xs text-navy/40 mt-2">Valor Net × visitas</p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border-gold/20 shadow-lg overflow-hidden">
        <CardHeader>
          <CardTitle className="font-heading text-xl text-navy flex items-center gap-2">
            <Wallet className="w-5 h-5 text-gold" />
            Breakdown por Parceiro
          </CardTitle>
        </CardHeader>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Parceiro</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="text-right">Visitas</TableHead>
                <TableHead className="text-right">Valor Net (a pagar)</TableHead>
                <TableHead className="text-right">Comissão PASS</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.breakdown || []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-navy/50">
                    Nenhum parceiro com visitas registadas
                  </TableCell>
                </TableRow>
              ) : (
                data.breakdown.map((b, i) => (
                  <TableRow key={b.partner_id || `unassigned-${i}`}>
                    <TableCell className="font-semibold">{b.partner_name || "—"}</TableCell>
                    <TableCell className="text-sm text-navy/70">{b.partner_email || "—"}</TableCell>
                    <TableCell className="text-right font-mono">{b.visits}</TableCell>
                    <TableCell className="text-right font-mono text-emerald-700 font-semibold">{fmt(b.net_due)}</TableCell>
                    <TableCell className="text-right font-mono text-gold font-semibold">{fmt(b.commission)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};

// =====================================================
// SettingsManagement — Perfil & 2FA management
// =====================================================
// Google/SEO settings card (GA4 + Search Console verification)
const GoogleSettingsCard = () => {
  const { token, API } = useAuth();
  const [ga4, setGa4] = useState("");
  const [verification, setVerification] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/public`).then(r => {
      setGa4(r.data.ga4_id || "");
      setVerification(r.data.google_site_verification || "");
    }).catch(() => {});
  }, [API]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, { ga4_id: ga4, google_site_verification: verification }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Definições Google guardadas");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-white border-gold/20 shadow-lg">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-lg text-navy">Google / SEO</CardTitle>
        <p className="text-navy/50 text-xs">Analytics e verificação do Search Console — sem precisar de alterar código.</p>
      </CardHeader>
      <CardContent>
        <form onSubmit={save} className="space-y-4 max-w-lg">
          <div className="space-y-1.5">
            <Label>Google Analytics 4 — Measurement ID</Label>
            <Input value={ga4} onChange={(e) => setGa4(e.target.value)} placeholder="G-XXXXXXXXXX" className="border-gold/30" data-testid="settings-ga4-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Google Search Console — código de verificação (meta tag)</Label>
            <Input value={verification} onChange={(e) => setVerification(e.target.value)} placeholder="conteúdo do meta google-site-verification" className="border-gold/30" data-testid="settings-verification-input" />
            <p className="text-[11px] text-navy/50">Alternativa recomendada: verificação por DNS no Search Console.</p>
          </div>
          <Button type="submit" disabled={saving} className="bg-gold hover:bg-gold-dark text-white" data-testid="settings-google-save">
            {saving ? "A guardar..." : "Guardar"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

const SettingsManagement = () => {
  const { user, token, API } = useAuth();
  const [step, setStep] = useState("idle"); // idle | scan | backup | disabling
  const [setupData, setSetupData] = useState(null); // {secret, qr_code_data_url}
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [disablePwd, setDisablePwd] = useState("");
  const [disableCode, setDisableCode] = useState("");
  // Local optimistic toggle so the badge updates immediately after activation
  const [localEnabled, setLocalEnabled] = useState(!!user?.totp_enabled);

  useEffect(() => { setLocalEnabled(!!user?.totp_enabled); }, [user?.totp_enabled]);

  const startActivation = async () => {
    setLoading(true);
    try {
      const resp = await axios.post(`${API}/auth/2fa/init`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSetupData(resp.data);
      setStep("scan");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao iniciar 2FA");
    } finally {
      setLoading(false);
    }
  };

  const confirmActivation = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setLoading(true);
    try {
      const resp = await axios.post(`${API}/auth/2fa/confirm`,
        { code },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBackupCodes(resp.data.backup_codes || []);
      setLocalEnabled(true);
      setStep("backup");
      setCode("");
      toast.success("2FA ativado com sucesso!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Código inválido");
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`,
        { password: disablePwd, code: disableCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("2FA desativado");
      setLocalEnabled(false);
      setStep("idle");
      setDisablePwd("");
      setDisableCode("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao desativar");
    } finally {
      setLoading(false);
    }
  };

  const copyCodes = () => {
    navigator.clipboard.writeText(backupCodes.join("\n"));
    toast.success("Códigos copiados");
  };

  const downloadCodes = () => {
    const text =
      "PASS ALGARVE — Códigos de Backup 2FA\n" +
      `Conta: ${user?.email}\n` +
      `Data: ${new Date().toLocaleString("pt-PT")}\n\n` +
      "Guarde estes códigos num local seguro. Cada código só pode ser usado UMA vez.\n\n" +
      backupCodes.map((c, i) => `${String(i + 1).padStart(2, "0")}. ${c}`).join("\n");
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "pass-algarve-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl text-navy mb-2">Definições</h1>
        <p className="text-navy/60 font-body">Perfil e segurança da conta admin</p>
      </div>

      <GoogleSettingsCard />

      <Card className="bg-white border-gold/20 shadow-lg">
        <CardHeader>
          <CardTitle className="font-heading text-xl text-navy flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-gold" />
            Segurança da Conta
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-heading text-sm text-navy font-semibold">Administrador</p>
              <p className="text-navy/70 text-sm">{user?.name} · {user?.email}</p>
            </div>
            <Badge className="bg-purple-100 text-purple-700">Admin</Badge>
          </div>

          {/* 2FA Section */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-heading text-sm text-navy font-semibold">Autenticação em Dois Fatores (2FA)</p>
                <p className="text-navy/70 text-sm">Google Authenticator · obrigatório para admin</p>
              </div>
              <Badge className={localEnabled ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                {localEnabled ? "Ativo" : "Não configurado"}
              </Badge>
            </div>

            {/* IDLE: show actions */}
            {step === "idle" && !localEnabled && (
              <Button
                onClick={startActivation}
                disabled={loading}
                className="bg-gold hover:bg-gold-dark text-white rounded-full"
                data-testid="settings-activate-2fa-btn"
              >
                {loading ? "Carregando..." : "Ativar 2FA"}
              </Button>
            )}

            {step === "idle" && localEnabled && (
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  onClick={() => setStep("disabling")}
                  className="border-red-300 text-red-600 hover:bg-red-50 rounded-full"
                  data-testid="settings-disable-2fa-btn"
                >
                  Desativar 2FA
                </Button>
              </div>
            )}

            {/* SCAN: QR + verify code */}
            {step === "scan" && setupData && (
              <div className="mt-4 space-y-4 border-t border-gray-200 pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                  <div className="bg-white rounded-xl p-4 flex justify-center border border-gold/20">
                    <img src={setupData.qr_code_data_url} alt="QR 2FA" className="w-48 h-48" data-testid="settings-2fa-qr" />
                  </div>
                  <div className="space-y-3">
                    <ol className="text-navy/70 font-body text-sm space-y-1 list-decimal list-inside">
                      <li>Abra o <strong>Google Authenticator</strong>, Authy ou 1Password</li>
                      <li>Toque em <strong>+</strong> → Ler código QR</li>
                      <li>Scaneie o QR ao lado</li>
                      <li>Insira o código de 6 dígitos abaixo</li>
                    </ol>
                    <div>
                      <p className="text-navy/50 text-xs mb-1">Ou insira manualmente:</p>
                      <p className="font-mono text-xs bg-white border border-gold/20 rounded px-2 py-1 break-all">{setupData.secret}</p>
                    </div>
                  </div>
                </div>
                <form onSubmit={confirmActivation} className="space-y-3">
                  <Input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="000000"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    className="border-gold/30 text-center text-2xl tracking-widest font-mono"
                    data-testid="settings-2fa-code-input"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      disabled={loading || code.length !== 6}
                      className="flex-1 bg-gold hover:bg-gold-dark text-white rounded-full"
                      data-testid="settings-2fa-confirm-btn"
                    >
                      {loading ? "Verificando..." : "Ativar 2FA"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setStep("idle"); setCode(""); setSetupData(null); }}
                      className="rounded-full"
                    >
                      Cancelar
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* BACKUP: show one-time codes */}
            {step === "backup" && (
              <div className="mt-4 space-y-4 border-t border-gray-200 pt-4">
                <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    <strong>Guarde estes códigos com segurança.</strong> São usados se perder o acesso ao autenticador. Cada código só funciona <strong>uma vez</strong> — não serão mostrados novamente.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2" data-testid="settings-2fa-backup-codes">
                  {backupCodes.map((c, i) => (
                    <div key={i} className="bg-white border border-gold/20 rounded-lg py-2 px-3 font-mono text-navy text-center text-sm">
                      {c}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button type="button" variant="outline" onClick={copyCodes} className="rounded-full" data-testid="settings-2fa-copy-codes">
                    <Copy className="w-4 h-4 mr-2" /> Copiar
                  </Button>
                  <Button type="button" variant="outline" onClick={downloadCodes} className="rounded-full" data-testid="settings-2fa-download-codes">
                    <Download className="w-4 h-4 mr-2" /> Download
                  </Button>
                </div>
                <Button onClick={() => setStep("idle")} className="w-full bg-gold hover:bg-gold-dark text-white rounded-full">
                  Concluído
                </Button>
              </div>
            )}

            {/* DISABLING */}
            {step === "disabling" && (
              <form onSubmit={handleDisable} className="mt-4 space-y-3 border-t border-gray-200 pt-4">
                <p className="text-sm text-red-700">Desativar 2FA reduz a segurança da sua conta. Insira a sua senha e um código atual para confirmar.</p>
                <Input
                  type="password"
                  placeholder="Senha actual"
                  value={disablePwd}
                  onChange={(e) => setDisablePwd(e.target.value)}
                  required
                  className="border-gold/30"
                  data-testid="settings-2fa-disable-pwd"
                />
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder="Código 2FA (6 dígitos)"
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ""))}
                  required
                  maxLength={6}
                  className="border-gold/30 font-mono text-center"
                  data-testid="settings-2fa-disable-code"
                />
                <div className="flex gap-2">
                  <Button type="submit" disabled={loading} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full" data-testid="settings-2fa-disable-confirm">
                    {loading ? "..." : "Desativar 2FA"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setStep("idle")} className="rounded-full">
                    Cancelar
                  </Button>
                </div>
              </form>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="bg-white border-red-200 shadow-lg" data-testid="danger-zone-card">
        <CardHeader>
          <CardTitle className="font-heading text-xl text-red-700 flex items-center gap-2">
            <Trash2 className="w-5 h-5" />
            Zona de Perigo
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="font-heading text-sm text-red-900 font-semibold mb-1">
              Resetar Dados Transacionais
            </p>
            <p className="text-red-800/80 text-sm mb-3 leading-relaxed">
              Apaga <b>todos</b> os passaportes vendidos, transações de pagamento e visitas registadas. Use apenas ao migrar de testes para produção real.<br />
              <b>Mantém intactos:</b> Atrações, Tipos de Passaporte, Parceiros, Utilizadores e configurações.
            </p>
            <FactoryResetButton />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Factory reset confirm flow component
const FactoryResetButton = () => {
  const { token, API } = useAuth();
  const [confirm, setConfirm] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleReset = async () => {
    if (confirm !== "RESET-TRANSACTIONS") {
      toast.error("Digite exatamente: RESET-TRANSACTIONS");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/admin/factory-reset-transactions`,
        { confirm: "RESET-TRANSACTIONS" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const d = res.data?.deleted || {};
      toast.success(`Reset concluído: ${d.passports || 0} passaportes e ${d.payment_transactions || 0} transações apagadas.`);
      setOpen(false);
      setConfirm("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao resetar");
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <Button
        onClick={() => setOpen(true)}
        className="bg-red-600 hover:bg-red-700 text-white rounded-full"
        data-testid="factory-reset-open-btn"
      >
        <Trash2 className="w-4 h-4 mr-2" /> Apagar Passaportes & Transações
      </Button>
    );
  }
  return (
    <div className="space-y-3 bg-white border border-red-300 rounded-lg p-3">
      <p className="text-sm text-red-900">
        Para confirmar, digite <code className="bg-red-100 px-1.5 py-0.5 rounded font-mono">RESET-TRANSACTIONS</code> abaixo:
      </p>
      <Input
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        placeholder="RESET-TRANSACTIONS"
        className="border-red-300 font-mono"
        data-testid="factory-reset-confirm-input"
      />
      <div className="flex gap-2">
        <Button
          onClick={handleReset}
          disabled={loading || confirm !== "RESET-TRANSACTIONS"}
          className="bg-red-600 hover:bg-red-700 text-white rounded-full disabled:opacity-50"
          data-testid="factory-reset-confirm-btn"
        >
          {loading ? "A apagar..." : "Sim, apagar tudo"}
        </Button>
        <Button
          onClick={() => { setOpen(false); setConfirm(""); }}
          variant="outline"
          className="rounded-full"
          data-testid="factory-reset-cancel-btn"
        >
          Cancelar
        </Button>
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Sessao encerrada com sucesso");
  };

  const navItems = [
    { icon: Home, label: "Dashboard", path: "/admin" },
    { icon: MapPin, label: "Atrações", path: "/admin/attractions" },
    { icon: Ticket, label: "Tipos Passaporte", path: "/admin/passport-types" },
    { icon: CreditCard, label: "Passaportes Vendidos", path: "/admin/passports-sold" },
    { icon: Receipt, label: "Transações", path: "/admin/transactions" },
    { icon: Wallet, label: "Financeiro", path: "/admin/financial" },
    { icon: UserPlus, label: "Parceiros", path: "/admin/partners" },
    { icon: Users, label: "Utilizadores", path: "/admin/users" },
    { icon: ShieldCheck, label: "Definições", path: "/admin/settings" },
  ];

  return (
    <div className="min-h-screen bg-sand">
      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-40 h-screen w-64 bg-navy transform transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/10">
            <PassAlgarveLogo size="small" />
            <Badge className="mt-2 bg-purple-500/20 text-purple-300">Admin</Badge>
          </div>
          
          <nav className="flex-1 p-4 space-y-2">
            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setSidebarOpen(false);
                }}
                className="sidebar-link w-full text-left"
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            ))}
          </nav>

          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 mb-4 px-4">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                <span className="text-purple-300 font-semibold">{user?.name?.[0]?.toUpperCase()}</span>
              </div>
              <div>
                <p className="text-white font-body text-sm">{user?.name}</p>
                <p className="text-white/50 text-xs">Administrador</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full text-white/70 hover:text-white hover:bg-white/10 justify-start"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Sair
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-navy p-4 flex items-center justify-between">
        <PassAlgarveLogoCompact />
        <button 
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-white p-2"
          data-testid="mobile-sidebar-toggle"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </header>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:ml-64 p-4 lg:p-8 pt-28 lg:pt-8">
        <div className="max-w-7xl mx-auto">
          <Routes>
            <Route index element={<AdminOverview />} />
            <Route path="attractions" element={<AttractionsManagement />} />
            <Route path="passport-types" element={<PassportTypesManagement />} />
            <Route path="passports-sold" element={<PassportsSoldManagement />} />
            <Route path="transactions" element={<TransactionsManagement />} />
            <Route path="financial" element={<FinancialManagement />} />
            <Route path="users" element={<UsersManagement />} />
            <Route path="partners" element={<PartnersManagement />} />
            <Route path="passports" element={<PassportsManagement />} />
            <Route path="settings" element={<SettingsManagement />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
